import React from 'react';
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  Package,
  RefreshCw,
  Search,
  ShieldCheck,
  Upload,
} from 'lucide-react';
import { dashboardService, InspectorDashboardSummary } from '../../../services/dashboardService';
import { inventoryService } from '../../../services/inventoryService';
import {
  InspectionReportDetail,
  InspectionCustodyContext,
  Form18RowPayload,
  inspectionService,
  InspectionMissionPayload,
} from '../../../services/inspectionService';
import { notificationService } from '../../../services/notificationService';
import { ChainOfCustodyEvent, DepartmentSummary, Firearm, InspectionMission } from '../../../types/domain';
import { todayISO } from '../../../utils/dateValidation';
import { markNotificationReadIfUnread } from '../../../utils/notificationEvents';
import { canonicalStatusKey, canonicalStatusLabel } from '../../../utils/statusPresentation';
import { useReferenceOptions } from '../../../hooks/useReferenceData';
import DismissibleError from '../../DismissibleError';
import {
  CompactTableToolbar,
  DetailDrawer,
  EmptyTableState,
  FirearmEvidencePanel,
  FirearmMasterImagePanel,
  NotificationInbox,
  NotificationInboxItem,
  FormModal,
  RecordsTableCard,
  StatusBadge,
  TypeBadge,
  WorkflowFeedback,
} from '../../records';

const today = todayISO;
const APPROVED_REPORT_TYPES = new Set(['reconciliation', 'inspection_compliance']);
const CHAIN_WORKFLOW_OPTIONS = [
  { value: 'issuance', label: 'Issuance' },
  { value: 'return', label: 'Return' },
  { value: 'incident', label: 'Incident' },
  { value: 'firearm_record_update', label: 'Firearm Record Update' },
  { value: 'officer_record_update', label: 'Officer Record Update' },
  { value: 'are_upload', label: 'ARE Upload' },
  { value: 'firearm_image_upload', label: 'Firearm Image Upload' },
];
const VALIDATION_SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];
const DISCREPANCY_STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'REFERRED', label: 'Referred' },
  { value: 'RESOLVED', label: 'Resolved' },
];

const value = (input: unknown, fallback = '-'): string =>
  input === null || input === undefined || input === '' ? fallback : String(input);

const cleanInventoryFilter = (filterValue: string) => filterValue.trim() || undefined;

const statusText = (status?: string) => value(status).replace(/_/g, ' ');

const titleText = (status?: string): string =>
  value(status).replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatDate = (date?: string | null): string => {
  if (!date) return '-';
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString();
};

const formatDateTime = (date?: string | null): string => {
  if (!date) return '-';
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleString();
};

const officerName = (officer: any): string =>
  `${officer.first_name || ''} ${officer.middle_name || ''} ${officer.last_name || ''}`.replace(/\s+/g, ' ').trim();

const FRSNote: React.FC<{ children: React.ReactNode }> = () => null;

const EmptyState: React.FC<{ title: string; copy: string }> = ({ title, copy }) => (
  <div className="workspace-empty">
    <Package size={22} />
    <strong>{title}</strong>
    <p>{copy}</p>
  </div>
);

const VALIDATION_BADGE_LABELS: Record<string, string> = {
  verified: 'No Discrepancy',
  conflict: 'Conflict',
  unrecorded: 'Unrecorded',
  new_discovery: 'New Discovery',
  incident_report_discrepancy: 'Incident Report Discrepancy',
  incident_status_mismatch: 'Incident Status Mismatch',
  officer_mismatch: 'Officer Mismatch',
  no_active_custody: 'No Active Custody',
  registry_assignment_mismatch: 'Registry Mismatch',
  department_mismatch: 'Department Mismatch',
  multiple_active_custody: 'Multiple Active Custody',
  unrecorded_firearm: 'New Discovery',
  custody_mismatch: 'Custody Mismatch',
  custody_integrity_error: 'Custody Integrity Error',
  firearm_not_registered: 'Unregistered Firearm',
};

const ValidationBadge: React.FC<{ result?: string }> = ({ result }) => {
  const normalized = (result || '').trim().toLowerCase();
  const label = VALIDATION_BADGE_LABELS[normalized] || 'Validation pending';
  const className = VALIDATION_BADGE_LABELS[normalized]
    ? normalized.replace(/[^a-z0-9_-]/g, '-')
    : 'pending';
  return <span className={`validation-badge ${className}`}>{label}</span>;
};

const CLEAN_VALIDATION_LABELS: Record<string, string> = {
  verified: 'No Discrepancy',
  conflict: 'Conflict',
  unrecorded: 'Unrecorded',
  new_discovery: 'Unrecorded',
  incident_report_discrepancy: 'Incident Report Discrepancy',
  incident_status_mismatch: 'Incident Status Mismatch',
};

const DISCREPANCY_TYPE_LABELS: Record<string, string> = {
  incident_report_discrepancy: 'Missing Incident Report',
  unrecorded_firearm: 'Unrecorded Firearm',
  UNRECORDED_FIREARM: 'Unrecorded Firearm',
  conflict: 'Conflict',
  CONFLICTED_ASSIGNMENT: 'Conflicted Assignment',
  NO_ACTIVE_CUSTODY: 'No Active Custody',
  DOCUMENT_COMPLIANCE: 'Document Compliance',
  PERSONNEL_COMPLIANCE: 'Personnel Compliance',
  CUSTODY_DATA_INTEGRITY: 'Custody Data Integrity',
  verified: 'No Discrepancy',
  incident_status_mismatch: 'Incident Status Mismatch',
  resolved_discrepancy: 'Discrepancy Resolved',
  new_discovery: 'Unrecorded Firearm',
};

type NotificationFilter = 'all' | 'inspection' | 'validation' | 'system';

const NOTIFICATION_FILTERS: Array<{ value: NotificationFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'inspection', label: 'Inspection Alerts' },
  { value: 'validation', label: 'Validation Alerts' },
];

const validationLabel = (result?: string): string => {
  const normalized = (result || '').trim().toLowerCase();
  return CLEAN_VALIDATION_LABELS[normalized] || titleText(result || 'pending');
};

const CUSTODY_LABELS: Record<string, string> = {
  verified: 'Verified',
  verified_candidate: 'Verified',
  custody_mismatch: 'Custody Mismatch',
  officer_mismatch: 'Officer Mismatch',
  no_active_custody: 'No Active Custody',
  registry_assignment_mismatch: 'Registry Mismatch',
  issuance_department_mismatch: 'Department Mismatch',
  department_mismatch: 'Department Mismatch',
  custody_integrity_error: 'Custody Integrity Error',
  multiple_active_custody: 'Multiple Active Custody',
  firearm_not_registered: 'Unregistered Firearm',
  unregistered_firearm: 'Unregistered Firearm',
};

const custodyLabel = (state?: string): string => (
  state ? (CUSTODY_LABELS[state] || titleText(state)) : 'Historical custody snapshot unavailable'
);

const discrepancyTypeLabel = (type?: string): string =>
  DISCREPANCY_TYPE_LABELS[type || ''] || titleText(type);

const validationTypeKey = (item: any): string => {
  if (
    item?.discrepancy_type === 'incident_report_discrepancy'
    && ['lost', 'stolen', 'beyond_economical_repair'].includes(item?.report_detail)
  ) {
    return item.report_detail;
  }
  return item?.discrepancy_type || '';
};

const validationTypeLabel = (item: any): string => {
  const key = validationTypeKey(item);
  if (key === 'lost') return 'Lost';
  if (key === 'stolen') return 'Stolen';
  if (key === 'beyond_economical_repair') return 'Damaged';
  return discrepancyTypeLabel(key);
};

const discrepancyReferenceLabel = (item: any): string => {
  if (Array.isArray(item?.discrepancy_ids) && item.discrepancy_ids.length > 0) {
    return item.discrepancy_ids.map((id: number) => `#${id}`).join(', ');
  }

  if (item?.discrepancy_id) {
    return `#${item.discrepancy_id}`;
  }

  const validationResult = String(item?.custody_validation_result || item?.validation_result || '').toLowerCase();
  if (validationResult && !['verified', 'no_discrepancy'].includes(validationResult)) {
    return 'Pending discrepancy link';
  }

  return 'No discrepancy';
};

const custodyReconciliationSummary = (item: any): string => {
  if (item?.metadata?.finding === 'same_firearm_multiple_observed_officers') return 'Same firearm observed under multiple officers';
  if (item?.metadata?.custody_reconciliation_required || item?.multi_issuance_case) return 'Custody reconciliation required';
  return '';
};

const custodyReconciliationDetailRows = (item: any): Array<[string, string]> => {
  const observedOfficers = Array.isArray(item?.metadata?.observed_officers)
    ? item.metadata.observed_officers
      .map((officer: any) => officer.officer_name || officer.badge_number)
      .filter(Boolean)
      .join(', ')
    : '';

  return [
    ['Custody finding', custodyReconciliationSummary(item)],
    ['Related Multi-Issuance Case', value(item?.multi_issuance_case, '')],
    ['Case status', titleText(item?.multi_issuance_case_status)],
    ['Resolution type', titleText(item?.multi_issuance_resolution_type)],
    ['Resolved at', formatDateTime(item?.multi_issuance_resolved_at)],
    ['Resolution notes', item?.multi_issuance_resolution_notes],
    ['Observed officers', observedOfficers],
  ].filter(([, detail]) => Boolean(detail && detail !== '-')) as Array<[string, string]>;
};

const expectedObservedValue = (officer?: string, status?: string): string => {
  const cleanOfficer = value(officer, '').trim();
  const cleanStatus = value(status, '').trim();
  if (!cleanOfficer && !cleanStatus) return '-';
  if (!cleanOfficer) return cleanStatus;
  if (!cleanStatus) return cleanOfficer;
  if (cleanOfficer.localeCompare(cleanStatus, undefined, { sensitivity: 'accent' }) === 0) {
    return cleanOfficer;
  }
  return `${cleanOfficer} | ${cleanStatus}`;
};

const notificationLabel = (type?: string): string =>
  DISCREPANCY_TYPE_LABELS[type || ''] || discrepancyTypeLabel(type);

const notificationCategory = (notification: any): NotificationFilter => {
  const type = String(notification.notification_type || notification.type || '').toLowerCase();
  if (['incident_report_discrepancy', 'unrecorded_firearm', 'new_discovery', 'conflict', 'incident_status_mismatch', 'resolved_discrepancy', 'verified'].some((key) => type.includes(key))) {
    return 'validation';
  }
  if (type.includes('inspection') || type.includes('form18') || type.includes('evidence')) return 'inspection';
  return 'system';
};

const relatedNotificationContext = (notification: any): string => {
  const parts = [
    notification.firearm_serial || notification.firearm_serial_number || notification.serial_number,
    notification.officer_name || notification.officer_badge,
    notification.department_name || notification.department || notification.target_station_unit,
  ].filter(Boolean);
  return parts.length > 0 ? `Related: ${parts.join(' | ')}` : '';
};

const typeTone = (type?: string): 'neutral' | 'blue' | 'green' | 'amber' | 'red' | 'purple' => {
  const normalized = (type || '').toLowerCase();
  if (normalized.includes('verified') || normalized.includes('resolved')) return 'green';
  if (normalized.includes('incident')) return 'amber';
  if (normalized.includes('unrecorded') || normalized.includes('lost') || normalized.includes('stolen')) return 'red';
  if (normalized.includes('conflict') || normalized.includes('mismatch')) return 'purple';
  return 'neutral';
};

const statusTone = (status?: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'purple' => {
  const normalized = (status || '').toLowerCase();
  if (normalized.includes('resolved') || normalized.includes('verified') || normalized.includes('closed')) return 'success';
  if (normalized.includes('open') || normalized.includes('pending')) return 'warning';
  if (normalized.includes('referred')) return 'purple';
  if (normalized.includes('rejected') || normalized.includes('unrecorded')) return 'danger';
  return 'neutral';
};

const REPORT_DETAIL_CHOICES: Array<{ value: InspectionReportDetail; label: string }> = [
  { value: 'verified', label: 'Routine physical inspection' },
  { value: 'unrecorded', label: 'Unrecorded firearm' },
  { value: 'lost', label: 'Lost' },
  { value: 'stolen', label: 'Stolen' },
  { value: 'beyond_economical_repair', label: 'Damaged' },
];

const REPORT_DETAILS_ALLOWING_BLANK_OFFICER = new Set<InspectionReportDetail>([
  'lost',
  'stolen',
  'beyond_economical_repair',
  'unrecorded',
]);

const requiresObservedOfficer = (reportDetail: InspectionReportDetail): boolean =>
  !REPORT_DETAILS_ALLOWING_BLANK_OFFICER.has(reportDetail);

const reportDetailLabel = (detail?: string): string => {
  if (detail === 'verified') {
    return 'Routine physical inspection';
  }
  if (detail === 'unrecorded') {
    return 'Unrecorded firearm';
  }
  return titleText(detail);
};

const areFileLabel = (report: any): string => {
  const fileName = report?.are_file_name || report?.are_file || report?.are_document_name;
  if (fileName) return String(fileName);
  if (report?.are_presented === false) return 'No ARE presented';
  return 'No file listed';
};

const supportingImageLabel = (report: any): string => {
  const fileName = report?.supporting_firearm_image_name || report?.supporting_firearm_image;
  return fileName ? String(fileName) : 'No image listed';
};

const evidencePreviewLabel = (filename: string, fallback: string): string => {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.pdf')) return 'View PDF';
  if (/\.(jpe?g|png)$/.test(lower)) return 'View Image';
  return fallback;
};

const DetailField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="detail-summary-field">
    <span>{label}</span>
    <strong>{children}</strong>
  </div>
);

const OfficerAutocomplete: React.FC<{
  value: string;
  officers: any[];
  onChange: (value: string) => void;
  label?: string;
  helperText?: string;
  error?: string;
}> = ({ value: currentValue, officers, onChange, label = 'Officer name or badge number', helperText = 'Search active officer names or badge numbers.', error }) => {
  const [open, setOpen] = React.useState(false);
  const query = currentValue.trim().toLowerCase();
  const matches = React.useMemo(() => officers.filter((officer) => {
    if (!query) return false;
    const name = officerName(officer);
    const badge = officer.badge_number || officer.badge || '';
    return `${name} ${badge}`.toLowerCase().includes(query);
  }).slice(0, 8), [officers, query]);

  const selectOfficer = (officer: any) => {
    const name = officerName(officer);
    onChange(officer.badge_number || officer.badge || name);
    setOpen(false);
  };

  return (
    <label className="officer-autocomplete"><span>{label}</span>
      <input
        value={currentValue}
        onChange={(event) => { onChange(event.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search officer name or badge"
        autoComplete="off"
      />
      {open && matches.length > 0 ? (
        <div className="officer-autocomplete-list" role="listbox">
          {matches.map((officer) => {
            const name = officerName(officer);
            const badge = officer.badge_number || officer.badge || '-';
            return (
              <button
                key={officer.id || `${name}-${badge}`}
                type="button"
                role="option"
                aria-selected={currentValue === badge || currentValue === name}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectOfficer(officer)}
              >
                {name || 'Unnamed Officer'} — {badge}
              </button>
            );
          })}
        </div>
      ) : null}
      {error ? <small className="field-error" role="alert">{error}</small> : <small>{helperText}</small>}
    </label>
  );
};

const MAX_EVIDENCE_SIZE = 5 * 1024 * 1024;
const ARE_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const IMAGE_FILE_TYPES = ['image/jpeg', 'image/png'];

const validateEvidenceFile = (file: File | null, allowedTypes: string[], label: string, required = false) => {
  if (!file) return required ? `${label} is required.` : '';
  if (!allowedTypes.includes(file.type)) return `${label} must be ${allowedTypes.includes('application/pdf') ? 'a PDF, JPEG, or PNG file' : 'a JPEG or PNG image'}.`;
  if (file.size > MAX_EVIDENCE_SIZE) return `${label} must not exceed 5 MB.`;
  return '';
};

const fieldErrorText = (value: unknown): string => {
  if (Array.isArray(value)) return value.map(fieldErrorText).filter(Boolean).join(' ');
  if (value && typeof value === 'object') return Object.values(value as Record<string, unknown>).map(fieldErrorText).filter(Boolean).join(' ');
  return value ? String(value) : '';
};

const submissionErrorText = (data: unknown): string => {
  if (!data) return 'Inspection submission failed validation.';
  if (typeof data === 'string') return data;
  if (Array.isArray(data)) return data.map(fieldErrorText).filter(Boolean).join(' ') || 'Inspection submission failed validation.';
  if (typeof data === 'object') {
    const payload = data as Record<string, unknown>;
    return fieldErrorText(payload.non_field_errors)
      || fieldErrorText(payload.officer_identifier)
      || fieldErrorText(payload.detail)
      || fieldErrorText(payload)
      || 'Inspection submission failed validation.';
  }
  return 'Inspection submission failed validation.';
};

export const InspectorDashboard: React.FC = () => {
  // FRS 2.1-2.4: dashboard metrics, status distribution, discrepancy alerts, recent inspection submissions.
  const [summary, setSummary] = React.useState<InspectorDashboardSummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setSummary(await dashboardService.getInspectorSummary());
    } catch {
      setError('Inspector dashboard summary is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  if (loading) return <div className="loading-state"><span className="loading-mark" />Loading inspector dashboard...</div>;

  if (error) {
    return <DismissibleError message={error} onDismiss={() => setError('')} onRetry={load} />;
  }

  if (!summary) {
    return <EmptyState title="No dashboard data" copy="Dashboard summary returned no records." />;
  }

  const metrics = [
    ['Submitted inspections', (summary as any).submitted_inspections ?? summary.recent_inspection_sessions.length, 'Inspection Reports', ClipboardCheck, 'blue'],
    ['Verified records', summary.verified_records, 'Validation Results', ShieldCheck, 'green'],
    ['Open discrepancies', (summary as any).open_discrepancies ?? summary.conflict_records, 'Validation Results', AlertTriangle, 'purple'],
    ['Unrecorded firearms', (summary as any).unrecorded_firearms ?? summary.new_discoveries, 'Validation Results', Search, 'neutral'],
  ] as const;

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading">
        <div>
          <h1>Inspector Dashboard</h1>
          <p>Inspection overview and validation signals.</p>
        </div>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} />Refresh data</button>
      </div>

      <div className="stats-grid inspector-stats">
        {metrics.map(([label, metric, note, Icon, tone]) => (
          <article className="stat-card" key={label}>
            <span className={`metric-icon ${tone}`}><Icon size={20} /></span>
            <div>
              <span className="stat-label">{label}</span>
              <strong className="stat-number">{metric}</strong>
              <span className="stat-note">{note}</span>
            </div>
          </article>
        ))}
      </div>

      <div className="dashboard-grid">
        <section className="panel">
          <div className="section-heading">
            <div>
              <h2>Validation Results</h2>
              <p>Verified records, conflicts, and new discoveries from inspection validation.</p>
            </div>
            <span>Summary</span>
          </div>
          <div className="module-list">
            <div className="module-row"><span className="module-icon"><ShieldCheck size={15} /></span><div><strong>Verified records</strong><p>{summary.verified_records} record(s)</p></div></div>
            <div className="module-row"><span className="module-icon"><AlertTriangle size={15} /></span><div><strong>Open discrepancies</strong><p>{(summary as any).open_discrepancies ?? summary.conflict_records} record(s)</p></div></div>
            <div className="module-row"><span className="module-icon"><Search size={15} /></span><div><strong>Incident report discrepancies</strong><p>{(summary as any).incident_report_discrepancies ?? 0} record(s)</p></div></div>
            <div className="module-row"><span className="module-icon"><Search size={15} /></span><div><strong>Unrecorded firearms</strong><p>{(summary as any).unrecorded_firearms ?? summary.new_discoveries} record(s)</p></div></div>
          </div>
        </section>

        <aside className="dashboard-side">
          <section className="panel">
            <div className="section-heading"><h2>Recent submitted inspections</h2><span>Inspection Reports</span></div>
            {((summary as any).recent_submitted_inspections || summary.recent_inspection_sessions).length === 0 ? (
              <EmptyState title="No recent submissions" copy="Submitted inspections will appear after validation." />
            ) : (
              <div className="health-list">
                {((summary as any).recent_submitted_inspections || summary.recent_inspection_sessions).map((session: any) => (
                  <div key={session.id}>
                    <span>{session.department || session.target_station_unit}<small>{session.inspection_outcome?.label || validationLabel(session.validation_result || session.status || '')}</small></span>
                    <strong>{session.firearm_serial_number || new Date(session.date_of_physical_count).toLocaleDateString()}</strong>
                  </div>
                ))}
              </div>
            )}
          </section>
        </aside>
      </div>
      <FRSNote>Inspector Dashboard maps to recent inspection sessions, verified records, conflict records, and new discoveries.</FRSNote>
    </div>
  );
};

export const InventoryReference: React.FC = () => {
  // FRS 3.1: Master Inventory List, read-only reference only.
  const [firearms, setFirearms] = React.useState<Firearm[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [filters, setFilters] = React.useState({ search: '', unit: '', status: '' });
  const [page, setPage] = React.useState(1);
  const [count, setCount] = React.useState(0);
  const pageSize = 8;
  const firearmStatusReference = useReferenceOptions('firearm_statuses');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await inventoryService.getFirearmsPage({
        search: cleanInventoryFilter(filters.search),
        deployment_unit: cleanInventoryFilter(filters.unit),
        status: cleanInventoryFilter(filters.status),
        page,
        page_size: pageSize,
      });
      setFirearms(result.results);
      setCount(result.count);
    } catch {
      setError('Inventory reference is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, [filters.search, filters.status, filters.unit, page]);

  React.useEffect(() => { void load(); }, [load]);

  const pageCount = Math.max(1, Math.ceil(count / pageSize));
  const rows = firearms;

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading">
        <div><h1>Inventory Reference</h1><p>Read-only firearm registry for inspection verification.</p></div>
      </div>
      <FRSNote>Inventory Reference maps to FRS 3.1 only. Add/edit inventory actions are intentionally hidden.</FRSNote>

      <RecordsTableCard
        title="Firearm Inventory Reference"
        description="Read-only registry records for inspection verification."
        meta={loading ? 'Loading' : `${count} firearm${count === 1 ? '' : 's'}`}
        toolbar={(
          <CompactTableToolbar
            searchValue={filters.search}
            searchPlaceholder="Search serial, brand, or model"
            onSearchChange={(nextSearch) => { setFilters({ ...filters, search: nextSearch }); setPage(1); }}
            filters={(
              <>
                <input aria-label="Deployment unit filter" value={filters.unit} onChange={(e) => { setFilters({ ...filters, unit: e.target.value }); setPage(1); }} placeholder="Deployment unit" />
                <select aria-label="Status filter" value={filters.status} disabled={firearmStatusReference.loading || Boolean(firearmStatusReference.error)} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}>
                  <option value="">Any status</option>
                  {firearmStatusReference.options.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </>
            )}
            resultCount={loading ? undefined : `${count} result${count === 1 ? '' : 's'}`}
          />
        )}
        footer={!loading && !error && firearms.length > 0 ? (
          <>
            <button className="btn btn-secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
            <span>Page {page} of {pageCount}</span>
            <button className="btn btn-secondary" disabled={page === pageCount} onClick={() => setPage(page + 1)}>Next</button>
          </>
        ) : null}
      >
        {firearmStatusReference.error ? (
          <DismissibleError message={firearmStatusReference.error} onDismiss={() => undefined} onRetry={firearmStatusReference.retry} />
        ) : null}
        <table className="records-table">
          <thead><tr><th>Serial Number</th><th>Brand / Model</th><th>Deployment Unit</th><th>Status</th><th>Assigned Officer</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5}><div className="loading-state"><span className="loading-mark" />Loading inventory...</div></td></tr>
            ) : error ? (
              <tr><td colSpan={5}><DismissibleError message={error} onDismiss={() => setError('')} onRetry={load} /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5}><EmptyTableState title="No firearms match the selected filters" description="Try adjusting the serial number, brand/model, deployment unit, or status filter." /></td></tr>
            ) : rows.map((firearm) => (
              <tr key={firearm.id}>
                <td>{firearm.serial_number}</td>
                <td>{value(firearm.model || firearm.article || firearm.description)}</td>
                <td>{value(firearm.assigned_to_unit || firearm.department_name)}</td>
                <td><StatusBadge status={canonicalStatusKey(firearm)} label={canonicalStatusLabel(firearm)} /></td>
                <td>{value(firearm.assigned_to_name || firearm.assigned_to_badge)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </RecordsTableCard>
    </div>
  );
};

export const InspectorChainOfCustody: React.FC = () => {
  const [departments, setDepartments] = React.useState<DepartmentSummary[]>([]);
  const [selectedDepartment, setSelectedDepartment] = React.useState('');
  const [logs, setLogs] = React.useState<ChainOfCustodyEvent[]>([]);
  const [firearms, setFirearms] = React.useState<Firearm[]>([]);
  const [selectedLog, setSelectedLog] = React.useState<ChainOfCustodyEvent | null>(null);
  const [loadingDepartments, setLoadingDepartments] = React.useState(true);
  const [loadingLogs, setLoadingLogs] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [eventTypeFilter, setEventTypeFilter] = React.useState('');
  const [officerFilter, setOfficerFilter] = React.useState('');
  const [firearmFilter, setFirearmFilter] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    inventoryService.getDepartments()
      .then((items) => setDepartments(items.filter((department) => department.is_active !== false).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => setError('Department options are temporarily unavailable.'))
      .finally(() => setLoadingDepartments(false));
  }, []);

  React.useEffect(() => {
    if (!selectedDepartment) {
      setLogs([]);
      setFirearms([]);
      return;
    }
    setLoadingLogs(true);
    setError('');
    Promise.all([
      inventoryService.getCustodyLogs({ department_id: selectedDepartment, require_department: true }),
      inventoryService.getFirearms({ department: Number(selectedDepartment) }),
    ])
      .then(([nextLogs, nextFirearms]) => { setLogs(nextLogs); setFirearms(nextFirearms || []); })
      .catch(() => setError('Chain-of-custody records are temporarily unavailable for the selected department.'))
      .finally(() => setLoadingLogs(false));
  }, [selectedDepartment]);

  const actorLabel = (log: ChainOfCustodyEvent): string =>
    value(log.performed_by_full_name || log.performed_by_username || log.performed_by_role_display, '');
  const holderLabel = (input: Record<string, any> | undefined, keys: string[]): string => {
    const source = input || {};
    const match = keys.map((key) => source[key]).find(Boolean);
    return value(match, '');
  };
  const multiIssuance = (log: ChainOfCustodyEvent): Record<string, any> => {
    const candidates = [log.metadata?.multi_issuance, log.new_state?.multi_issuance, log.previous_state?.multi_issuance, log.transaction_data?.multi_issuance];
    return candidates.find((item) => item && (item.detected || item.flagged || item.resolved)) || {};
  };
  const multiDetailValue = (log: ChainOfCustodyEvent, keys: string[]): string => {
    const multi = multiIssuance(log);
    const direct = keys.map((key) => multi[key]).find(Boolean);
    if (direct) return value(direct, '');
    const details = multi.details || {};
    for (const detail of Object.values(details) as Record<string, any>[]) {
      const match = keys.map((key) => detail?.[key]).find(Boolean);
      if (match) return value(match, '');
    }
    return '';
  };
  const isMultiIssuanceEvent = (log: ChainOfCustodyEvent): boolean => {
    const decision = value(log.transaction_data?.decision, '').toLowerCase();
    const issuanceStatus = value(log.new_state?.issuance_status, '').toLowerCase();
    const multi = multiIssuance(log);
    return Boolean(multi.detected || multi.flagged || multi.resolved || log.transaction_data?.multi_issuance_detected || decision.includes('multi_issuance') || issuanceStatus === 'multi_issuance');
  };
  const fromHolder = (log: ChainOfCustodyEvent): string => {
    if (log.from_display) return log.from_display;
    if (isMultiIssuanceEvent(log)) {
      return multiDetailValue(log, ['current_holder_name', 'current_officer_name', 'officer_name'])
        || holderLabel(log.previous_state, ['officer_name', 'assigned_to_name', 'holder_name', 'custodian_name'])
        || '—';
    }
    if (log.workflow_type === 'issuance' && log.action_type === 'approved') return value(log.department_name, '—');
    if (log.workflow_type === 'return' && ['confirmed', 'status_changed', 'submitted'].includes(log.action_type || '')) {
      return value(log.officer_name || holderLabel(log.previous_state, ['officer_name', 'assigned_to_name', 'holder_name', 'custodian_name']), '—');
    }
    return holderLabel(log.previous_state, ['officer_name', 'assigned_to_name', 'holder_name', 'custodian_name', 'department_name', 'location_name']) || '—';
  };
  const toHolder = (log: ChainOfCustodyEvent): string => {
    if (log.to_display) return log.to_display;
    if (isMultiIssuanceEvent(log)) return 'No custody transfer';
    if (log.workflow_type === 'issuance' && log.action_type === 'approved') return value(log.officer_name, '—');
    if (log.workflow_type === 'return' && ['confirmed', 'status_changed', 'submitted'].includes(log.action_type || '')) {
      return value(log.department_name || holderLabel(log.new_state, ['department_name', 'location_name']), '—');
    }
    return holderLabel(log.new_state, ['officer_name', 'assigned_to_name', 'holder_name', 'custodian_name', 'department_name', 'location_name']) || '—';
  };
  const movementSummary = (log: ChainOfCustodyEvent): string =>
    value(log.movement_summary, isMultiIssuanceEvent(log) ? 'No custody transfer: Conflict — Multi-Issuance was handled by Logistics.' : `${fromHolder(log)} to ${toHolder(log)}`);
  const relatedId = (log: ChainOfCustodyEvent, pattern: RegExp): string => (
    pattern.test(value(log.source_model, '')) ? value(log.source_record_id, '') : ''
  );

  const visibleLogs = logs.filter((log) => {
    const eventType = value(log.workflow_type_display || log.workflow_type, '');
    const officer = value(log.officer_name || log.officer_badge, '');
    const firearm = value(log.firearm_serial, '');
    const created = value(log.created_at || log.timestamp, '').slice(0, 10);
    const query = [
      firearm,
      eventType,
      log.action_type_display,
      officer,
      actorLabel(log),
      fromHolder(log),
      toHolder(log),
      log.movement_summary,
      log.department_name,
      log.remarks,
    ].join(' ').toLowerCase();
    return query.includes(search.toLowerCase())
      && (!eventTypeFilter || log.workflow_type === eventTypeFilter)
      && (!officerFilter || officer.toLowerCase().includes(officerFilter.toLowerCase()))
      && (!firearmFilter || firearm.toLowerCase().includes(firearmFilter.toLowerCase()))
      && (!dateFrom || created >= dateFrom)
      && (!dateTo || created <= dateTo);
  });

  const clearFilters = () => {
    setSearch('');
    setEventTypeFilter('');
    setOfficerFilter('');
    setFirearmFilter('');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Chain of Custody</h1><p>Read-only custody records by selected department.</p></div></div>
      <FRSNote>Inspector custody access is read-only and department-scoped by explicit selection.</FRSNote>
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <RecordsTableCard
        title="Department Selection"
        description="Select a department before loading chain-of-custody records."
        meta={loadingDepartments ? 'Loading departments' : `${departments.length} active`}
      >
        <div className="inspector-form">
          <label className="full-width">Department
            <select
              aria-label="Department"
              value={selectedDepartment}
              onChange={(event) => setSelectedDepartment(event.target.value)}
            >
              <option value="">Select department</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>{department.name}{department.code ? ` (${department.code})` : ''}</option>
              ))}
            </select>
          </label>
        </div>
      </RecordsTableCard>
      {!selectedDepartment ? (
        <section className="panel"><EmptyState title="Select a department" copy="Select a department to view chain-of-custody records." /></section>
      ) : (
        <RecordsTableCard
          title="Chain-of-Custody Records"
          description="Read-only custody events for the selected department."
          meta={loadingLogs ? 'Loading' : `${visibleLogs.length} of ${logs.length}`}
          toolbar={(
            <CompactTableToolbar
              searchValue={search}
              searchPlaceholder="serial, officer, user, remarks"
              onSearchChange={setSearch}
              filters={(
                <>
                  <select aria-label="Event type filter" value={eventTypeFilter} onChange={(event) => setEventTypeFilter(event.target.value)}>
                    <option value="">All event types</option>
                    {CHAIN_WORKFLOW_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                  <input aria-label="Officer search" value={officerFilter} onChange={(event) => setOfficerFilter(event.target.value)} placeholder="Officer name" />
                  <input aria-label="Firearm serial search" value={firearmFilter} onChange={(event) => setFirearmFilter(event.target.value)} placeholder="Serial number" />
                  <input aria-label="Date from" type="date" value={dateFrom} max={dateTo || today()} onChange={(event) => setDateFrom(event.target.value)} />
                  <input aria-label="Date to" type="date" value={dateTo} min={dateFrom || undefined} max={today()} onChange={(event) => setDateTo(event.target.value)} />
                </>
              )}
              actions={<button className="btn btn-secondary" type="button" onClick={clearFilters}>Clear</button>}
            />
          )}
        >
          <table className="records-table">
            <thead><tr><th>Date / Time</th><th>Firearm Serial</th><th>Event Type</th><th>From</th><th>To</th><th>Officer / User</th><th>Department</th><th>Remarks</th><th>Action</th></tr></thead>
            <tbody>
              {loadingLogs ? (
                <tr><td colSpan={9}><div className="loading-state"><span className="loading-mark" />Loading custody records...</div></td></tr>
              ) : visibleLogs.length === 0 ? (
                <tr><td colSpan={9}><EmptyTableState title="No custody records" description="No records matched the selected department and filters." /></td></tr>
              ) : visibleLogs.map((log) => (
                <tr key={log.id}>
                  <td>{formatDateTime(log.created_at || log.timestamp)}</td>
                  <td>{value(log.firearm_serial)}</td>
                  <td><TypeBadge type={log.workflow_type || ''} label={value(log.workflow_type_display || titleText(log.workflow_type))} tone={typeTone(log.workflow_type)} /></td>
                  <td>{fromHolder(log)}</td>
                  <td>{toHolder(log)}</td>
                  <td>{value(log.officer_name || actorLabel(log))}</td>
                  <td>{value(log.department_name)}</td>
                  <td>{value(log.remarks, 'No remarks')}</td>
                  <td><button className="btn btn-secondary" type="button" onClick={() => setSelectedLog(log)}>View Details</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </RecordsTableCard>
      )}
      <DetailDrawer
        open={Boolean(selectedLog)}
        title="Custody Event Details"
        subtitle={selectedLog ? value(selectedLog.firearm_serial) : undefined}
        meta={selectedLog ? <TypeBadge type={selectedLog.workflow_type || ''} label={value(selectedLog.workflow_type_display || titleText(selectedLog.workflow_type))} tone={typeTone(selectedLog.workflow_type)} /> : null}
        onClose={() => setSelectedLog(null)}
      >
        {selectedLog && (
          <div className="detail-summary-grid">
            <DetailField label="Event type">{value(selectedLog.workflow_type_display || titleText(selectedLog.workflow_type))}</DetailField>
            <DetailField label="Action">{value(selectedLog.action_type_display || titleText(selectedLog.action_type))}</DetailField>
            <DetailField label="Event date/time">{formatDateTime(selectedLog.created_at || selectedLog.timestamp)}</DetailField>
            <DetailField label="Firearm serial">{value(selectedLog.firearm_serial)}</DetailField>
            <DetailField label="Firearm article/model/caliber">{[selectedLog.firearm_article, selectedLog.firearm_model, selectedLog.firearm_caliber].filter(Boolean).join(' / ') || '-'}</DetailField>
            <DetailField label="Department">{value(selectedLog.department_name)}</DetailField>
            <DetailField label="From holder">{fromHolder(selectedLog)}</DetailField>
            <DetailField label="To holder">{toHolder(selectedLog)}</DetailField>
            <DetailField label="Custody movement summary">{movementSummary(selectedLog)}</DetailField>
            <DetailField label="Officer involved">{value(selectedLog.officer_name || selectedLog.officer_badge)}</DetailField>
            <DetailField label="Performed by">{actorLabel(selectedLog)}</DetailField>
            <DetailField label="Related issuance ID">{relatedId(selectedLog, /issuance/i)}</DetailField>
            <DetailField label="Related return ID">{relatedId(selectedLog, /return/i)}</DetailField>
            <DetailField label="Related incident ID">{relatedId(selectedLog, /incident/i)}</DetailField>
            {isMultiIssuanceEvent(selectedLog) && (
              <>
                <DetailField label="Attempted holder">{value(multiDetailValue(selectedLog, ['attempted_holder_name', 'attempted_officer_name']), '—')}</DetailField>
                <DetailField label="Reason">Conflict — Multi-Issuance</DetailField>
                <DetailField label="Resolution">{value(multiIssuance(selectedLog).resolution_message, 'Request rejected after Logistics review')}</DetailField>
                <DetailField label="Current holder">{fromHolder(selectedLog)}</DetailField>
                <DetailField label="Blocking issuance ID">{value(multiDetailValue(selectedLog, ['blocking_issuance_id', 'blocking_issuance_ids']), '—')}</DetailField>
                <DetailField label="Resolved by">{value(multiIssuance(selectedLog).resolved_by_display || multiIssuance(selectedLog).resolved_by_name || multiIssuance(selectedLog).resolved_by, '—')}</DetailField>
                <DetailField label="Resolved at">{formatDateTime(multiIssuance(selectedLog).resolved_at)}</DetailField>
                <DetailField label="Resolution method">{value(titleText(multiIssuance(selectedLog).resolution_method), '—')}</DetailField>
                <DetailField label="Resolution notes">{value(multiIssuance(selectedLog).resolution_notes || multiIssuance(selectedLog).resolution_note, '—')}</DetailField>
              </>
            )}
            <DetailField label="Remarks / notes">{value(selectedLog.remarks, 'No remarks')}</DetailField>
            <DetailField label="Created at">{formatDate(selectedLog.created_at)}</DetailField>
            <DetailField label="Audit metadata">{value(selectedLog.event_hash, 'No hash listed')}</DetailField>
            {relatedId(selectedLog, /issuance/i) ? (
              <div className="full-width">
                <FirearmMasterImagePanel
                  image={firearms.find((item) => item.serial_number === selectedLog.firearm_serial)?.primary_image}
                  firearmSerialNumber={selectedLog.firearm_serial || undefined}
                />
              </div>
            ) : null}
            {relatedId(selectedLog, /return/i) ? (
              <div className="full-width">
                <FirearmEvidencePanel
                  contextType="return"
                  contextId={Number(relatedId(selectedLog, /return/i))}
                  firearmSerialNumber={selectedLog.firearm_serial}
                  readOnly
                />
              </div>
            ) : null}
          </div>
        )}
      </DetailDrawer>
    </div>
  );
};

export const InspectionMissions: React.FC = () => {
  // FRS 5.1: New Inspection Report session and mission list.
  const [missions, setMissions] = React.useState<InspectionMission[]>([]);
  const [departments, setDepartments] = React.useState<DepartmentSummary[]>([]);
  const [form, setForm] = React.useState<InspectionMissionPayload>({ target_station_unit: '', department: 0, date_of_physical_count: today(), remarks: '' });
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [missionRows, departmentRows] = await Promise.all([
        inspectionService.getInspectionMissions(),
        inspectionService.getDepartments(),
      ]);
      setMissions(missionRows);
      setDepartments(departmentRows.filter((department: DepartmentSummary) => department.is_active));
    } catch {
      setError('Inspection missions are temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const createMission = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage('');
    setError('');
    if (form.date_of_physical_count > today()) {
      setError('Date of physical count cannot be later than today.');
      return;
    }
    if (!form.target_station_unit.trim()) {
      setError('Target station/unit is required.');
      return;
    }
    if (!form.department) {
      setError('Department is required.');
      return;
    }
    try {
      await inspectionService.createInspectionMission(form);
      setMessage('Inspection report session created.');
      setForm({ target_station_unit: '', department: 0, date_of_physical_count: today(), remarks: '' });
      await load();
    } catch {
      setError('Could not create the inspection report session.');
    }
  };

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Inspection Missions</h1><p>Start and review inspection report sessions.</p></div></div>
      <FRSNote>Inspection Missions maps to FRS 5.1.</FRSNote>
      {message && <div className="info-message">{message}</div>}
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <div className="dashboard-grid">
        <section className="panel">
          <div className="section-heading"><h2>Session details</h2></div>
          <form className="inspector-form" onSubmit={createMission}>
            <label><span>Target station/unit</span><input value={form.target_station_unit} onChange={(e) => setForm({ ...form, target_station_unit: e.target.value })} placeholder="Station or unit name" /></label>
            <label>
              <span>Department</span>
              <select value={form.department} onChange={(e) => setForm({ ...form, department: Number(e.target.value) })}>
                <option value={0}>Select department</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>{department.name}</option>
                ))}
              </select>
            </label>
            <label><span>Date of physical count</span><input type="date" max={today()} value={form.date_of_physical_count} onChange={(e) => setForm({ ...form, date_of_physical_count: e.target.value })} /></label>
            <label className="full-width"><span>Remarks</span><textarea value={form.remarks || ''} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Optional session notes" /></label>
            <div className="form-actions full-width"><button className="btn btn-primary" type="submit"><ClipboardCheck size={14} />Create session</button></div>
          </form>
        </section>
        <section className="panel">
          <div className="section-heading"><h2>Existing sessions</h2></div>
          {loading ? <div className="loading-state"><span className="loading-mark" />Loading missions...</div> : missions.length === 0 ? (
            <EmptyState title="No missions yet" copy="New inspection sessions will appear here." />
          ) : (
            <div className="module-list">
              {missions.slice(0, 8).map((mission) => (
                <div className="module-row" key={mission.id}>
                  <span className="module-icon"><ClipboardCheck size={15} /></span>
                  <div><strong>{mission.target_station_unit}</strong><p>{value(mission.department_name)} - {mission.date_of_physical_count} - {mission.status}</p></div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export const Form18Editor: React.FC = () => {
  // FRS 5.2-5.3: structured Form 18 row encoding and master-record validation.
  const [missions, setMissions] = React.useState<InspectionMission[]>([]);
  const [rows, setRows] = React.useState<any[]>([]);
  const [form, setForm] = React.useState<Form18RowPayload>({
    mission: 0,
    officer_name_input: '',
    entered_firearm_serial_number: '',
    physical_status: 'serviceable',
    are_presented: false,
    remarks: '',
  });
  const [error, setError] = React.useState('');
  const [selectedRow, setSelectedRow] = React.useState<any | null>(null);

  React.useEffect(() => {
    inspectionService.getInspectionMissions().then((missionRows) => {
      setMissions(missionRows);
      setForm((current) => ({ ...current, mission: current.mission || missionRows[0]?.id || 0 }));
    }).catch(() => setError('Could not load inspection missions.'));
  }, []);

  const loadRows = React.useCallback(async () => {
    if (!form.mission) return;
    setRows(await inspectionService.getForm18Rows({ mission: form.mission }));
  }, [form.mission]);

  React.useEffect(() => { void loadRows().catch(() => setError('Could not load Form 18 rows.')); }, [loadRows]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (!form.mission) {
      setError('Select an inspection mission before encoding Form 18.');
      return;
    }
    try {
      const result = await inspectionService.createForm18Row(form);
      setRows((current) => [result, ...current]);
      setForm((current) => ({ ...current, officer_name_input: '', entered_firearm_serial_number: '', remarks: '', are_presented: false }));
    } catch (requestError: any) {
      const data = requestError?.originalError?.response?.data || requestError?.response?.data;
      setError(data ? JSON.stringify(data) : 'Backend validation could not save this Form 18 row.');
    }
  };

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Form 18 Editor</h1><p>Structured inspection grid with backend validation states.</p></div></div>
      <FRSNote>Form 18 Editor maps to FRS 5.2 and 5.3. Validation results come from existing backend rules only.</FRSNote>
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <section className="panel">
        <form className="inspector-form" onSubmit={submit}>
          <label><span>Mission</span><select value={form.mission} onChange={(e) => setForm({ ...form, mission: Number(e.target.value) })}><option value={0}>Select mission</option>{missions.map((mission) => <option key={mission.id} value={mission.id}>{mission.target_station_unit} - {mission.date_of_physical_count}</option>)}</select></label>
          <label><span>Inspected police officer name</span><input value={form.officer_name_input || ''} onChange={(e) => setForm({ ...form, officer_name_input: e.target.value })} placeholder="Canonical officer name" /></label>
          <label><span>Verified firearm serial number</span><input value={form.entered_firearm_serial_number} onChange={(e) => setForm({ ...form, entered_firearm_serial_number: e.target.value })} placeholder="Serial number" /></label>
          <label><span>Physical status</span><select value={form.physical_status} onChange={(e) => setForm({ ...form, physical_status: e.target.value })}><option value="serviceable">Serviceable</option><option value="defective">Defective</option><option value="missing_parts">Unserviceable</option></select></label>
          <label className="checkbox-line"><input type="checkbox" checked={form.are_presented} onChange={(e) => setForm({ ...form, are_presented: e.target.checked })} />ARE presented</label>
          <label><span>Remarks</span><input value={form.remarks || ''} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Inspection remarks" /></label>
          <div className="form-actions full-width"><button className="btn btn-primary" type="submit"><CheckCircle2 size={14} />Validate and save row</button></div>
        </form>
      </section>
      <section className="panel inspector-section-gap">
        <div className="section-heading"><h2>Inspection grid rows</h2><span>Validation</span></div>
        {rows.length === 0 ? <EmptyState title="No Form 18 rows" copy="Rows will show green, yellow, or red validation states after backend validation." /> : (
          <div className="frs-table-wrap">
            <table><thead><tr><th>Serial</th><th>Observed officer</th><th>Expected officer</th><th>Physical</th><th>ARE</th><th>Custody</th><th>Evidence</th><th>Action</th></tr></thead>
              <tbody>{rows.map((row) => <tr key={row.id}><td>{row.entered_firearm_serial_number}</td><td>{value(row.inspected_officer_name, 'Unresolved')}</td><td>{value(row.expected_officer_name, 'No active custodian')}</td><td>{statusText(row.physical_status)}</td><td>{row.are_presented ? 'Presented' : 'Not presented'}</td><td><ValidationBadge result={row.custody_validation_result || row.custody_snapshot?.validation_state || row.validation_result} /></td><td>{row.evidence_count || 0}</td><td><button className="btn btn-secondary" type="button" onClick={() => setSelectedRow(row)}>View Details</button></td></tr>)}</tbody>
            </table>
          </div>
        )}
      </section>
      <DetailDrawer open={Boolean(selectedRow)} title="Form 18 Custody Details" subtitle={selectedRow?.entered_firearm_serial_number} onClose={() => setSelectedRow(null)}>
        {selectedRow && <div className="inspection-custody-detail">
          <h3>Observed at Inspection</h3>
          <div className="detail-summary-grid"><DetailField label="Officer">{value(selectedRow.inspected_officer_name, 'Unresolved')}</DetailField><DetailField label="Badge">{value(selectedRow.inspected_officer_badge_number)}</DetailField><DetailField label="Physical status">{titleText(selectedRow.physical_status)}</DetailField><DetailField label="ARE">{selectedRow.are_presented ? 'Presented' : 'Not presented'}</DetailField></div>
          <h3>Expected at Inspection</h3>
          <div className="detail-summary-grid"><DetailField label="Officer">{value(selectedRow.expected_officer_name, 'No active custodian')}</DetailField><DetailField label="Badge">{value(selectedRow.expected_officer_badge_number)}</DetailField><DetailField label="Issuance">{selectedRow.authoritative_issuance ? `#${selectedRow.authoritative_issuance}` : 'None'}</DetailField><DetailField label="Validation">{custodyLabel(selectedRow.custody_snapshot?.validation_state)}</DetailField></div>
          <h3>Current Custody</h3>
          <div className="detail-summary-grid"><DetailField label="Officer">{value(selectedRow.current_custody?.expected_officer_name, 'No active custodian')}</DetailField><DetailField label="Issuance">{selectedRow.current_custody?.issuance_id ? `#${selectedRow.current_custody.issuance_id}` : 'None'}</DetailField><DetailField label="Changed after inspection">{selectedRow.custody_changed_after_inspection ? 'Yes' : 'No'}</DetailField><DetailField label="Evidence count">{selectedRow.evidence_count || 0}</DetailField></div>
          <h3>Reconciliation</h3><p>{selectedRow.validation_message}</p>
        </div>}
      </DetailDrawer>
    </div>
  );
};

export const InspectionReports: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState<'new' | 'submitted' | 'files'>('new');
  const [departments, setDepartments] = React.useState<any[]>([]);
  const [submissions, setSubmissions] = React.useState<any[]>([]);
  const [officers, setOfficers] = React.useState<any[]>([]);
  const [selectedReport, setSelectedReport] = React.useState<any | null>(null);
  const [preview, setPreview] = React.useState<{
    open: boolean;
    loading: boolean;
    error: string;
    url: string;
    contentType: string;
    filename: string;
    title: string;
  }>({ open: false, loading: false, error: '', url: '', contentType: '', filename: '', title: '' });
  const [result, setResult] = React.useState<any | null>(null);
  const [fileInputKey, setFileInputKey] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [firearmsLoading, setFirearmsLoading] = React.useState(false);
  const [firearmOptions, setFirearmOptions] = React.useState<Firearm[]>([]);
  const [custodyContext, setCustodyContext] = React.useState<InspectionCustodyContext | null>(null);
  const [custodyLoading, setCustodyLoading] = React.useState(false);
  const [custodyError, setCustodyError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [fieldErrors, setFieldErrors] = React.useState<{ officer_identifier?: string }>({});
  const [form, setForm] = React.useState({
    department: 0,
    actual_inspection_date: today(),
    firearm_serial_number: '',
    officer_identifier: '',
    report_detail: 'verified' as InspectionReportDetail,
    remarks: '',
    are_presented: true,
    are_file: null as File | null,
    supporting_firearm_image: null as File | null,
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [departmentRows, submittedRows, officerRows] = await Promise.all([
        inspectionService.getDepartments(),
        inspectionService.getInspectionSubmissions(),
        inventoryService.getOfficers().catch(() => []),
      ]);
      setDepartments(departmentRows.filter((department: any) => department.is_active !== false));
      setSubmissions(submittedRows);
      setOfficers(officerRows.filter((officer: any) =>
        (!officer.employment_status || officer.employment_status === 'active') && officer.status !== 'inactive'
      ));
      setForm((current) => ({
        ...current,
        department: current.department || departmentRows[0]?.id || 0,
      }));
    } catch {
      setError('Inspection submission data is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  React.useEffect(() => () => {
    if (preview.url) URL.revokeObjectURL(preview.url);
  }, [preview.url]);

  React.useEffect(() => {
    if (!form.department) {
      setFirearmOptions([]);
      setFirearmsLoading(false);
      return;
    }
    let active = true;
    setFirearmsLoading(true);
    inventoryService.getFirearms({ department: form.department })
      .then((items) => {
        if (active) setFirearmOptions(items);
      })
      .catch(() => {
        if (active) setFirearmOptions([]);
      })
      .finally(() => {
        if (active) setFirearmsLoading(false);
      });
    return () => { active = false; };
  }, [form.department]);

  const selectedFirearm = React.useMemo(
    () => firearmOptions.find((firearm) => firearm.serial_number === form.firearm_serial_number),
    [firearmOptions, form.firearm_serial_number]
  );
  const selectedObservedOfficer = React.useMemo(() => {
    const normalized = form.officer_identifier.trim().toLowerCase();
    if (!normalized) return undefined;
    return officers.find((officer) => {
      const badge = String(officer.badge_number || officer.badge || '').trim().toLowerCase();
      return badge === normalized || officerName(officer).trim().toLowerCase() === normalized;
    });
  }, [form.officer_identifier, officers]);

  React.useEffect(() => {
    if (!selectedFirearm || !form.department) {
      setCustodyContext(null);
      setCustodyError('');
      return;
    }

    let active = true;
    setCustodyLoading(true);
    setCustodyError('');

    inspectionService.getCustodyContext({
      firearm_id: selectedFirearm.id,
      department_id: form.department,
      observed_officer_id: selectedObservedOfficer?.id,
    }).then((context) => {
      if (active) setCustodyContext(context);
    }).catch(() => {
      if (active) {
        setCustodyContext(null);
        setCustodyError('Authoritative custody context is temporarily unavailable.');
      }
    }).finally(() => {
      if (active) setCustodyLoading(false);
    });

    return () => {
      active = false;
    };
  }, [form.department, selectedFirearm, selectedObservedOfficer]);


  const updateDepartment = (department: number) => {
    setForm((current) => ({ ...current, department, firearm_serial_number: '', officer_identifier: '' }));
    setCustodyContext(null);
  };

  const updateReportDetail = (report_detail: InspectionReportDetail) => {
    setForm((current) => ({
      ...current,
      report_detail,
      officer_identifier: report_detail === 'unrecorded' ? '' : current.officer_identifier,
    }));
  };

  const updateFirearmSerial = (firearm_serial_number: string) => {
    setForm((current) => ({
      ...current,
      firearm_serial_number,

    }));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setFieldErrors({});
    setResult(null);
    if (!form.department) return setError('Department is required.');
    if (form.actual_inspection_date > today()) return setError('Actual inspection date cannot be later than today.');
    if (!form.firearm_serial_number.trim()) return setError('Firearm serial number is required.');
    if (requiresObservedOfficer(form.report_detail) && !form.officer_identifier.trim()) {
      setFieldErrors({ officer_identifier: 'Physically observed officer is required for routine inspections.' });
      return setError('Physically observed officer is required for routine inspections.');
    }
    if (form.are_presented && !form.are_file) {
      return setError('ARE file upload is required unless No ARE presented is selected.');
    }
    const areError = validateEvidenceFile(form.are_file, ARE_FILE_TYPES, 'ARE file upload');
    if (areError) return setError(areError);
    const imageError = validateEvidenceFile(form.supporting_firearm_image, IMAGE_FILE_TYPES, 'Supporting firearm image');
    if (imageError) return setError(imageError);
    if (custodyContext && custodyContext.validation_state !== 'verified') {
      const confirmed = window.confirm(
        `${custodyLabel(custodyContext.validation_state)}. Submitting will create a discrepancy for Logistics review. Continue?`
      );
      if (!confirmed) return;
    }

    setSubmitting(true);
    try {
      const response = await inspectionService.submitInspection({
        department: form.department,
        actual_inspection_date: form.actual_inspection_date,
        firearm_serial_number: form.firearm_serial_number,
        officer_identifier: form.officer_identifier.trim(),

        report_detail: form.report_detail,
        remarks: form.remarks,
        are_presented: form.are_presented,
        are_file: form.are_file as File,
        supporting_firearm_image: form.supporting_firearm_image,
      });
      setResult(response);
      setForm((current) => ({
        ...current,
        firearm_serial_number: '',
        officer_identifier: '',
        remarks: '',
        are_presented: true,
        are_file: null,
        supporting_firearm_image: null,
      }));
      setFileInputKey((key) => key + 1);
      await load();
      setActiveTab('submitted');
    } catch (requestError: any) {
      const data = requestError?.originalError?.response?.data || requestError?.response?.data;
      const officerMessage = Array.isArray(data?.officer_identifier)
        ? String(data.officer_identifier[0] || '')
        : String(data?.officer_identifier || '');
      if (officerMessage) {
        setFieldErrors({ officer_identifier: officerMessage });
      }
      setError(submissionErrorText(data));
    } finally {
      setSubmitting(false);
    }
  };

  const closePreview = () => {
    setPreview((current) => {
      if (current.url) URL.revokeObjectURL(current.url);
      return { open: false, loading: false, error: '', url: '', contentType: '', filename: '', title: '' };
    });
  };

  const openEvidencePreview = async (report: any, kind: 'ARE' | 'photo') => {
    const filename = kind === 'ARE' ? areFileLabel(report) : supportingImageLabel(report);
    const title = kind === 'ARE' ? 'ARE file preview' : 'Supporting image preview';
    setPreview((current) => {
      if (current.url) URL.revokeObjectURL(current.url);
      return { open: true, loading: true, error: '', url: '', contentType: '', filename, title };
    });
    try {
      const blob = await inspectionService.getInspectionEvidencePreview(Number(report.id), kind);
      if (blob.type && blob.type.includes('application/json')) {
        setPreview((current) => ({ ...current, loading: false, error: 'Evidence file could not be loaded.' }));
        return;
      }
      const url = URL.createObjectURL(blob);
      setPreview((current) => ({
        ...current,
        loading: false,
        error: '',
        url,
        contentType: blob.type || '',
      }));
    } catch {
      setPreview((current) => ({
        ...current,
        loading: false,
        error: 'Evidence file could not be loaded or was not found.',
      }));
    }
  };

  const renderEvidenceAction = (report: any, kind: 'ARE' | 'photo') => {
    const filename = kind === 'ARE' ? areFileLabel(report) : supportingImageLabel(report);
    if (kind === 'ARE' && report?.are_presented === false && !report?.are_file_name && !report?.are_file && !report?.are_document_name) {
      return 'No ARE presented';
    }
    if (kind === 'photo' && filename === 'No image listed') return 'No image listed';
    if (kind === 'ARE' && filename === 'No file listed') return 'No file listed';
    return (
      <button
        type="button"
        className="document-preview-link"
        title={filename}
        onClick={() => void openEvidencePreview(report, kind)}
      >
        {evidencePreviewLabel(filename, kind === 'ARE' ? 'View Document' : 'View Image')}
      </button>
    );
  };

  const renderNewInspection = () => (
    <section className="panel">
      <div className="section-heading"><div><h2>New Inspection</h2><p>Evidence is attached during submission when an ARE is presented.</p></div><span>One-by-one</span></div>
      {loading ? <div className="loading-state"><span className="loading-mark" />Loading inspection form...</div> : (
        <form className="inspector-form" onSubmit={submit}>
          <label><span>Department</span><select value={form.department} onChange={(e) => updateDepartment(Number(e.target.value))}><option value={0}>Select department</option>{departments.map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}</select></label>
          <label><span>Actual inspection date</span><input type="date" max={today()} value={form.actual_inspection_date} onChange={(e) => setForm({ ...form, actual_inspection_date: e.target.value })} /></label>
          {form.report_detail === 'unrecorded' ? (
            <label><span>Observed unrecorded serial number</span><input value={form.firearm_serial_number} onChange={(e) => setForm({ ...form, firearm_serial_number: e.target.value })} placeholder="Observed serial number" /></label>
          ) : (
            <label><span>Firearm serial number</span><select value={form.firearm_serial_number} onChange={(e) => updateFirearmSerial(e.target.value)} disabled={!form.department || firearmsLoading}>
              <option value="">{!form.department ? 'Select department first' : firearmsLoading ? 'Loading firearms...' : 'Select firearm serial number'}</option>
              {!firearmsLoading && form.department && firearmOptions.length === 0 && <option value="" disabled>No firearms available for selected department</option>}
              {firearmOptions.map((firearm) => <option key={firearm.id} value={firearm.serial_number}>{firearm.serial_number} - {value(firearm.article || firearm.model, 'Firearm')} - {value(firearm.assigned_to_name, 'Unassigned')}</option>)}
            </select></label>
          )}
          <OfficerAutocomplete
            value={form.officer_identifier}
            officers={officers}
            onChange={(officer_identifier) => {
              setFieldErrors((current) => ({ ...current, officer_identifier: undefined }));
              setForm((current) => ({ ...current, officer_identifier }));
            }}
            label="Physically Observed Officer or Badge Number"
            error={fieldErrors.officer_identifier}
            helperText={
              requiresObservedOfficer(form.report_detail)
                ? 'Enter the officer physically observed with the firearm.'
                : 'Officer may be left blank when no physical holder was identified.'
            }
          />

          <label><span>Report detail</span><select value={form.report_detail} onChange={(e) => updateReportDetail(e.target.value as InspectionReportDetail)}>{REPORT_DETAIL_CHOICES.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}</select></label>
          {selectedFirearm && (
            <section className="custody-context-panel full-width" aria-label="Authoritative Custody">
              <div className="custody-context-media">
                <FirearmMasterImagePanel image={selectedFirearm.primary_image} firearmSerialNumber={selectedFirearm.serial_number} />
              </div>
              <div className="custody-context-details">
                <div className="section-heading">
                  <div><h3>Authoritative Custody</h3><p>Read-only custody recomputed by the backend.</p></div>
                  {custodyContext && <StatusBadge status={custodyContext.validation_state} label={custodyLabel(custodyContext.validation_state)} tone={custodyContext.validation_state === 'verified' ? 'success' : custodyContext.validation_state === 'multiple_active_custody' ? 'danger' : 'warning'} />}
                </div>
                {custodyLoading && <div className="loading-state"><span className="loading-mark" />Checking custody...</div>}
                {custodyError && <div className="error-message" role="alert"><span className="error-body">{custodyError}</span></div>}
                {custodyContext && !custodyLoading && (
                  <div className="detail-summary-grid custody-summary-grid">
                    <DetailField label="Active issuance">{custodyContext.issuance_id ? `#${custodyContext.issuance_id} - ${titleText(custodyContext.issuance_status)}` : 'None'}</DetailField>
                    <DetailField label="Authoritative custodian">{value(custodyContext.expected_officer_name, 'No active custodian')}</DetailField>
                    <DetailField label="Authoritative badge">{value(custodyContext.expected_officer_badge_number)}</DetailField>
                    <DetailField label="Registry assigned officer">{value(custodyContext.registry_assigned_to_name, 'Unassigned')}</DetailField>
                    <DetailField label="Firearm department">{value(custodyContext.department_name)}</DetailField>
                    <DetailField label="ARE status">{titleText(custodyContext.are_status)}</DetailField>
                    {custodyContext.reasons.length > 0 && (
                      <div className={`custody-warning full-width ${custodyContext.validation_state === 'multiple_active_custody' ? 'critical' : ''}`} role="alert">
                        <AlertTriangle size={18} />
                        <div><strong>{custodyLabel(custodyContext.validation_state)}</strong>{custodyContext.reasons.map((reason) => <p key={reason}>{reason}</p>)}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}
          <label className="checkbox-line full-width"><input type="checkbox" checked={!form.are_presented} onChange={(e) => setForm({ ...form, are_presented: !e.target.checked, are_file: e.target.checked ? null : form.are_file })} />No ARE presented</label>
          <label className="full-width"><span>ARE file upload</span><input key={`are-${fileInputKey}-${form.are_presented ? 'presented' : 'not-presented'}`} type="file" accept="application/pdf,image/jpeg,image/png" disabled={!form.are_presented} onChange={(e) => setForm({ ...form, are_file: e.target.files?.[0] || null })} />{!form.are_presented && <small>No upload is needed when no ARE was presented.</small>}</label>
          <label className="full-width"><span>Optional supporting firearm image</span><input key={`supporting-${fileInputKey}`} type="file" accept="image/jpeg,image/png" onChange={(e) => setForm({ ...form, supporting_firearm_image: e.target.files?.[0] || null })} /></label>
          <label className="full-width"><span>Remarks</span><textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Optional inspection remarks" /></label>
          <div className="form-actions full-width"><button className="btn btn-primary" type="submit" disabled={submitting}><Upload size={14} />{submitting ? 'Submitting...' : 'Submit inspection'}</button></div>
        </form>
      )}
    </section>
  );

  const renderSubmittedReports = () => (
    <RecordsTableCard
      title="Submitted Inspection Reports"
      description="Click a submitted report to review evidence and validation details."
      meta={`${submissions.length} report${submissions.length === 1 ? '' : 's'}`}
    >
      <table className="records-table">
        <thead><tr><th>Date</th><th>Department</th><th>Serial</th><th>Observed</th><th>Expected</th><th>Inspection-Time Custody</th><th>Detail</th><th>Action</th></tr></thead>
        <tbody>
          {submissions.length === 0 ? (
            <tr><td colSpan={8}><EmptyTableState title="No submitted inspections" description="Submitted inspection reports will appear here after validation." /></td></tr>
          ) : submissions.map((item) => (
            <tr className="clickable-row" key={item.id} onClick={() => setSelectedReport(item)}>
              <td>{value(item.actual_inspection_date)}</td>
              <td>{value(item.department_name)}</td>
              <td>{value(item.firearm_serial_number || item.firearm_serial)}</td>
              <td>{value(item.officer_name || item.officer_identifier)}</td>
              <td>{value(item.expected_officer_summary?.name, 'No active custodian')}</td>
              <td>
                {item.custody_validation_result || item.custody_validation_state
                  ? <ValidationBadge result={item.custody_validation_result || item.custody_validation_state} />
                  : <span className="validation-badge pending">Historical snapshot unavailable</span>}
              </td>
              <td><TypeBadge type={item.report_detail || ''} label={reportDetailLabel(item.report_detail)} tone={typeTone(item.report_detail)} /></td>
              <td><button className="btn btn-secondary" type="button" onClick={(event) => { event.stopPropagation(); setSelectedReport(item); }}>View</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </RecordsTableCard>
  );

  return (
    <div className="admin-dashboard inspector-workspace generated-files-workspace">
      <div className="page-heading">
      <div><h1>Inspection Reports</h1><p>Submit inspections one record at a time with optional ARE evidence.</p></div>
      </div>
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      {result && (
        <div className="info-message">
          Custody assessment: {titleText(result.custody_validation_result || result.validation_result)}. {result.validation_message}
        </div>
      )}
      <div className="records-segmented-tabs" role="tablist" aria-label="Inspection report sections">
        <button type="button" role="tab" aria-selected={activeTab === 'new'} className={activeTab === 'new' ? 'active' : ''} onClick={() => setActiveTab('new')}>New Inspection</button>
        <button type="button" role="tab" aria-selected={activeTab === 'submitted'} className={activeTab === 'submitted' ? 'active' : ''} onClick={() => setActiveTab('submitted')}>Submitted Reports</button>
        <button type="button" role="tab" aria-selected={activeTab === 'files'} className={activeTab === 'files' ? 'active' : ''} onClick={() => setActiveTab('files')}>Generated Files</button>
      </div>
      {activeTab === 'new' && renderNewInspection()}
      {activeTab === 'submitted' && renderSubmittedReports()}
      {activeTab === 'files' && <InspectorReports embedded />}
      <DetailDrawer
        open={Boolean(selectedReport)}
        title="Inspection Report Summary"
        subtitle={selectedReport ? value(selectedReport.firearm_serial_number || selectedReport.firearm_serial) : undefined}
        meta={selectedReport ? <TypeBadge type={selectedReport.report_detail || ''} label={reportDetailLabel(selectedReport.report_detail)} tone={typeTone(selectedReport.report_detail)} /> : null}
        onClose={() => setSelectedReport(null)}
      >
        {selectedReport && (
          <div className="inspection-custody-detail">
            <h3>Observed at Inspection</h3>
            <div className="detail-summary-grid">
              <DetailField label="Date">{value(selectedReport.actual_inspection_date)}</DetailField>
              <DetailField label="Department">{value(selectedReport.department_name)}</DetailField>
              <DetailField label="Firearm serial">{value(selectedReport.firearm_serial_number || selectedReport.firearm_serial)}</DetailField>
              <DetailField label="Observed officer">
                {value(
                  selectedReport.observed_officer?.name
                    || selectedReport.officer_name
                    || selectedReport.officer_identifier,
                  'No observed officer',
                )}
              </DetailField>
              <DetailField label="Observed badge">
                {value(selectedReport.observed_officer?.badge_number || selectedReport.officer_badge)}
              </DetailField>
              <DetailField label="Report detail">{reportDetailLabel(selectedReport.report_detail)}</DetailField>
              <DetailField label="Validation">{validationLabel(selectedReport.validation_result)}</DetailField>
            </div>

            <h3>Authoritative Snapshot at Inspection</h3>
            <div className="detail-summary-grid">
              <DetailField label="Custody assessment">
                {custodyLabel(
                  selectedReport.custody_validation_result
                    || selectedReport.custody_validation_state,
                )}
              </DetailField>
              <DetailField label="Custody match">
                {selectedReport.custody_match === null || selectedReport.custody_match === undefined
                  ? 'Not applicable'
                  : selectedReport.custody_match
                    ? 'Matched'
                    : 'Mismatch'}
              </DetailField>
              <DetailField label="Authoritative custodian">
                {value(
                  selectedReport.authoritative_officer_name
                    || selectedReport.expected_officer_summary?.name,
                  'No active custodian',
                )}
              </DetailField>
              <DetailField label="Authoritative badge">
                {value(
                  selectedReport.authoritative_officer_badge_number
                    || selectedReport.expected_officer_summary?.badge_number,
                )}
              </DetailField>
              <DetailField label="Active issuance">
                {selectedReport.authoritative_issuance_summary
                  ? `#${selectedReport.authoritative_issuance_summary.id} - ${titleText(selectedReport.authoritative_issuance_summary.status)}`
                  : selectedReport.authoritative_issuance
                    ? `#${selectedReport.authoritative_issuance}`
                    : 'None'}
              </DetailField>
              <DetailField label="Registry status">
                {titleText(selectedReport.registry_status_at_inspection)}
              </DetailField>
              <DetailField label="Checked at">{formatDateTime(selectedReport.custody_checked_at)}</DetailField>
              <DetailField label="LTOPF status at inspection">
                {titleText(selectedReport.observed_ltopf_status)}
              </DetailField>
              <DetailField label="LTOPF expiration at inspection">
                {formatDate(selectedReport.observed_ltopf_expiration_date)}
              </DetailField>
              <DetailField label="ARE status at inspection">
                {titleText(selectedReport.authoritative_are_status)}
              </DetailField>
              <DetailField label="ARE expiration at inspection">
                {formatDate(selectedReport.authoritative_are_expiration_date)}
              </DetailField>
              <DetailField label="Related Multi-Issuance Case">
                {value(selectedReport.related_multi_issuance_case, 'No related case')}
              </DetailField>
              <DetailField label="Related case status">
                {titleText(selectedReport.related_multi_issuance_case_status)}
              </DetailField>
              <DetailField label="Custody reconciliation">
                {selectedReport.custody_reconciliation_required ? 'Required' : 'Not required'}
              </DetailField>
              <DetailField label="Compliance follow-up">
                {selectedReport.compliance_followup_required ? 'Required' : 'Not required'}
              </DetailField>
              <DetailField label="Assessment reason">
                {value(
                  selectedReport.validation_message
                    || selectedReport.validation_explanation,
                  'No mismatch recorded',
                )}
              </DetailField>
            </div>

            <h3>Current Custody</h3>
            <div className="detail-summary-grid">
              <DetailField label="Current custodian">
                {value(selectedReport.current_custody?.expected_officer_name, 'No active custodian')}
              </DetailField>
              <DetailField label="Current issuance">
                {selectedReport.current_custody?.issuance_id
                  ? `#${selectedReport.current_custody.issuance_id} - ${titleText(selectedReport.current_custody.issuance_status)}`
                  : 'None'}
              </DetailField>
              <DetailField label="Custody changed after inspection">
                {selectedReport.custody_changed_after_inspection === null
                  || selectedReport.custody_changed_after_inspection === undefined
                  ? 'Historical snapshot unavailable'
                  : selectedReport.custody_changed_after_inspection
                    ? 'Yes'
                    : 'No'}
              </DetailField>
              <DetailField label="Current firearm status">
                {titleText(selectedReport.current_custody?.firearm_status)}
              </DetailField>
            </div>

            <h3>Evidence and Reconciliation</h3>
            <div className="detail-summary-grid">
              <DetailField label="ARE file">{renderEvidenceAction(selectedReport, 'ARE')}</DetailField>
              <DetailField label="Supporting image">{renderEvidenceAction(selectedReport, 'photo')}</DetailField>
              <DetailField label="Registry images">
                {selectedReport.evidence_groups?.registry_image?.length || 0}
              </DetailField>
              <DetailField label="Transaction evidence">
                {selectedReport.evidence_groups?.transaction_evidence?.length || 0}
              </DetailField>
              <DetailField label="Inspection evidence">
                {selectedReport.evidence_groups?.inspection_evidence?.length || 0}
              </DetailField>
              <DetailField label="Discrepancies">
                {discrepancyReferenceLabel(selectedReport)}
              </DetailField>
              <DetailField label="Remarks">{value(selectedReport.remarks, 'No remarks')}</DetailField>
            </div>

          </div>
        )}
      </DetailDrawer>
      {preview.open && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={preview.title}>
          <div className="modal-content evidence-preview-modal">
            <div className="modal-header">
              <div>
                <h2>{preview.title}</h2>
                <p title={preview.filename}>{preview.filename}</p>
              </div>
              <button type="button" className="modal-close" aria-label="Close preview" onClick={closePreview}>x</button>
            </div>
            <div className="evidence-preview-body">
              {preview.loading && <div className="loading-state"><span className="loading-mark" />Loading evidence preview...</div>}
              {preview.error && <div className="error-message" role="alert"><span className="error-body">{preview.error}</span></div>}
              {!preview.loading && !preview.error && preview.url && (
                preview.contentType.includes('pdf') || preview.filename.toLowerCase().endsWith('.pdf') ? (
                  <iframe title={preview.filename} src={preview.url} className="evidence-preview-frame" />
                ) : preview.contentType.startsWith('image/') || /\.(jpe?g|png)$/i.test(preview.filename) ? (
                  <img src={preview.url} alt={preview.filename} className="evidence-preview-image" />
                ) : (
                  <p>Preview is not available for this file type.</p>
                )
              )}
            </div>
            {preview.url && <div className="form-actions"><a className="btn btn-secondary" href={preview.url} target="_blank" rel="noreferrer">Open document in new tab</a></div>}
          </div>
        </div>
      )}
    </div>
  );
};

export const EvidenceUpload: React.FC = () => {
  // FRS 5.4: JPEG/PNG evidence uploads, max 3 images per inspected firearm row, max 5 MB each.
  const [rows, setRows] = React.useState<any[]>([]);
  const [selectedRow, setSelectedRow] = React.useState(0);
  const [files, setFiles] = React.useState<File[]>([]);
  const [evidence, setEvidence] = React.useState<any[]>([]);
  const [error, setError] = React.useState('');
  const [status, setStatus] = React.useState('');

  React.useEffect(() => {
    inspectionService.getForm18Rows().then(setRows).catch(() => setError('Could not load Form 18 rows for evidence upload.'));
  }, []);

  React.useEffect(() => {
    if (selectedRow) inspectionService.getEvidenceRecords({ row: selectedRow }).then(setEvidence).catch(() => setEvidence([]));
  }, [selectedRow]);

  const validateFiles = (nextFiles: File[]) => {
    if (nextFiles.length > 3) return 'Upload up to 3 images per inspected firearm.';
    const invalidType = nextFiles.find((file) => !['image/jpeg', 'image/png'].includes(file.type));
    if (invalidType) return 'Only JPEG and PNG files are supported.';
    const tooLarge = nextFiles.find((file) => file.size > 5 * 1024 * 1024);
    if (tooLarge) return 'Each image must not exceed 5 MB.';
    return '';
  };

  const onFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFiles = Array.from(event.target.files || []);
    const validation = validateFiles(nextFiles);
    setError(validation);
    setFiles(validation ? [] : nextFiles);
  };

  const upload = async () => {
    setError('');
    setStatus('');
    if (!selectedRow) {
      setError('Select a Form 18 row before uploading evidence.');
      return;
    }
    const validation = validateFiles(files);
    if (validation) {
      setError(validation);
      return;
    }
    try {
      for (const file of files) {
        await inspectionService.uploadEvidence({ form18_row: selectedRow, image: file });
      }
      setStatus('Evidence upload completed.');
      setFiles([]);
      setEvidence(await inspectionService.getEvidenceRecords({ row: selectedRow }));
    } catch {
      setError('Evidence upload failed. Backend validation may have rejected the file or max-count rule.');
    }
  };

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Evidence Upload</h1><p>Upload firearm evidence and view uploaded evidence.</p></div></div>
      <FRSNote>Photographic Evidence maps to FRS 5.4. PDF evidence is intentionally rejected.</FRSNote>
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      {status && <div className="info-message">{status}</div>}
      <section className="panel">
        <div className="inspector-form">
          <label className="full-width"><span>Inspected firearm row</span><select value={selectedRow} onChange={(e) => setSelectedRow(Number(e.target.value))}><option value={0}>Select Form 18 row</option>{rows.map((row) => <option key={row.id} value={row.id}>{row.entered_firearm_serial_number} - {row.inspected_officer_name}</option>)}</select></label>
          <label className="full-width"><span>Evidence images</span><input type="file" multiple accept="image/jpeg,image/png" onChange={onFiles} /></label>
          <div className="form-actions full-width"><button className="btn btn-primary" type="button" onClick={upload}><Upload size={14} />Upload evidence</button></div>
        </div>
      </section>
      <section className="panel inspector-section-gap">
        <div className="section-heading"><h2>Evidence list</h2><span>API supported</span></div>
        {evidence.length === 0 ? <EmptyState title="No evidence images" copy="Uploaded evidence for the selected row will appear here." /> : (
          <div className="module-list">{evidence.map((item) => <div className="module-row" key={item.id}><span className="module-icon"><Camera size={15} /></span><div><strong>{item.original_filename}</strong><p>{item.mime_type} - {item.file_size} bytes</p></div></div>)}</div>
        )}
      </section>
    </div>
  );
};

export const PhotographicEvidence = EvidenceUpload;

interface ValidationResultsProps {
  allowActions?: boolean;
  embedded?: boolean;
  custodyOnly?: boolean;
}

export const ValidationResults: React.FC<ValidationResultsProps> = ({
  allowActions = false,
  embedded = false,
  custodyOnly = false,
}) => {
  // FRS 6.1: reconciliation report and discrepancy categories.
  const [discrepancies, setDiscrepancies] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState('');
  const [severityFilter, setSeverityFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [departmentFilter, setDepartmentFilter] = React.useState('');
  const [evidenceFilter, setEvidenceFilter] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [selectedDiscrepancy, setSelectedDiscrepancy] = React.useState<any | null>(null);
  const [error, setError] = React.useState('');
  const [departments, setDepartments] = React.useState<DepartmentSummary[]>([]);
  const [officers, setOfficers] = React.useState<any[]>([]);
  const [actionMode, setActionMode] = React.useState<'retain' | 'refer' | 'revise' | null>(null);
  const [actionNotes, setActionNotes] = React.useState('');
  const [targetOfficerId, setTargetOfficerId] = React.useState<number | null>(null);
  const [correctionWorkflow, setCorrectionWorkflow] = React.useState<'ISSUANCE' | 'RETURN' | 'UNSPECIFIED'>('ISSUANCE');
  const [correctionInstruction, setCorrectionInstruction] = React.useState('');
  const [actionLoading, setActionLoading] = React.useState(false);
  const [actionError, setActionError] = React.useState('');
  const [actionMessage, setActionMessage] = React.useState('');
  const [revalidationFailure, setRevalidationFailure] = React.useState<any | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [nextDiscrepancies, nextDepartments, nextOfficers] = await Promise.all([
        inspectionService.getDiscrepancies(custodyOnly ? { custody_only: true } : {}),
        inspectionService.getDepartments(),
        inventoryService.getOfficers().catch(() => []),
      ]);
      setDiscrepancies(nextDiscrepancies);
      setDepartments(nextDepartments.filter((department: DepartmentSummary) => department.is_active !== false));
      setOfficers(nextOfficers.filter((officer: any) => (
        (!officer.employment_status || officer.employment_status === 'active')
        && officer.status !== 'inactive'
      )));
    } catch {
      setError('Inspection validation results are temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, [custodyOnly]);

  React.useEffect(() => { void load(); }, [load]);

  const correctionOfficerOptions = React.useMemo(() => officers.filter((officer: any) => (
    !selectedDiscrepancy?.department || officer.department === selectedDiscrepancy.department
  )), [officers, selectedDiscrepancy]);

  const openAction = (mode: 'retain' | 'refer' | 'revise') => {
    setActionMode(mode);
    setActionNotes(mode === 'revise' ? selectedDiscrepancy?.referral_notes || '' : '');
    setActionError('');
    const workflow = mode === 'revise' && selectedDiscrepancy?.correction_workflow
      ? selectedDiscrepancy.correction_workflow
      : 'ISSUANCE';
    setCorrectionWorkflow(workflow);
    setCorrectionInstruction(
      mode === 'revise' ? selectedDiscrepancy?.correction_instruction || '' : '',
    );
    setTargetOfficerId(
      mode === 'revise'
        ? selectedDiscrepancy?.correction_target_officer || null
        : selectedDiscrepancy?.officer || null,
    );
  };

  const actionErrorMessage = (err: any): string => {
    const data = err?.response?.data || err?.originalError?.response?.data || err;
    if (typeof data?.message === 'string') return data.message;
    for (const field of ['resolution_notes', 'referral_notes', 'correction_workflow', 'correction_instruction', 'correction_target_officer', 'custody', 'status', 'discrepancy']) {
      const detail = data?.[field];
      if (Array.isArray(detail) && detail[0]) return String(detail[0]);
      if (typeof detail === 'string') return detail;
    }
    return 'The reconciliation action could not be completed.';
  };

  const submitAction = async () => {
    if (!selectedDiscrepancy || !actionMode) return;
    if (actionMode !== 'revise' && !actionNotes.trim()) {
      setActionError(actionMode === 'retain' ? 'Resolution notes are required.' : 'Referral notes are required.');
      return;
    }
    if (actionMode !== 'retain' && !correctionInstruction.trim()) {
      setActionError('Correction instruction is required.');
      return;
    }
    if (actionMode !== 'retain' && correctionWorkflow === 'ISSUANCE' && !targetOfficerId) {
      setActionError('Target officer is required for an Issuance plan.');
      return;
    }
    setActionLoading(true);
    setActionError('');
    try {
      let updated;
      if (actionMode === 'retain') {
        updated = await inspectionService.retainAuthoritativeCustody(selectedDiscrepancy.id, {
          resolution_notes: actionNotes.trim(),
        });
      } else {
        const planPayload = {
          correction_workflow: correctionWorkflow,
          correction_instruction: correctionInstruction.trim(),
          correction_target_officer: correctionWorkflow === 'RETURN' ? null : targetOfficerId,
        };
        updated = actionMode === 'refer'
          ? await inspectionService.referForCustodyCorrection(selectedDiscrepancy.id, {
          referral_notes: actionNotes.trim(),
            ...planPayload,
          })
          : await inspectionService.prescribeCorrectionPlan(
            selectedDiscrepancy.id,
            planPayload,
          );
      }
      setSelectedDiscrepancy(updated);
      setActionMode(null);
      setActionMessage(
        actionMode === 'retain'
          ? 'Authoritative custody retained. The inspection discrepancy is resolved without a custody transfer.'
          : actionMode === 'revise'
            ? 'Correction Plan revised. The current prescription time boundary has been reset.'
            : 'Discrepancy referred with a Correction Plan. Current custody remains unchanged until the proper workflow is completed.',
      );
      await load();
    } catch (err) {
      setActionError(actionErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const revalidate = async () => {
    if (!selectedDiscrepancy) return;
    setActionLoading(true);
    setActionError('');
    setRevalidationFailure(null);
    try {
      const updated = await inspectionService.revalidateAfterCorrection(selectedDiscrepancy.id);
      setSelectedDiscrepancy(updated);
      setActionMessage('Custody correction verified. Current authoritative custody now satisfies the reconciliation target.');
      await load();
    } catch (err: any) {
      const failure = err?.response?.data;
      if (err?.response?.status === 409 && failure?.code === 'RECONCILIATION_REVALIDATION_FAILED') {
        setRevalidationFailure(failure);
        setActionError('Correction not yet verified');
      } else {
        setActionError(actionErrorMessage(err));
      }
    } finally {
      setActionLoading(false);
    }
  };

  const recordsTitle = custodyOnly ? 'Inspection Findings' : 'Inspection validation records';
  const recordsDescription = custodyOnly
    ? 'Custody and registry findings that may require Logistics reconciliation.'
    : allowActions
      ? 'Review custody discrepancies and coordinate authorized correction workflows.'
      : 'Inspector access is read-only. Use filters to scan discrepancy categories quickly.';

  const discrepancyTypeOptions = React.useMemo(() => {
    const unique = new Map<string, string>();
    discrepancies.forEach((item) => {
      const key = validationTypeKey(item);
      if (key) unique.set(key, validationTypeLabel(item));
    });
    return [...unique.entries()]
      .map(([optionValue, label]) => ({ value: optionValue, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [discrepancies]);

  const visibleDiscrepancies = React.useMemo(() => discrepancies.filter((item) => {
    const query = [
      validationTypeLabel(item),
      item.description,
      item.report_detail,
      item.severity,
      item.department_name,
      item.source_serial_number,
      item.expected_officer_name,
      item.expected_status,
      item.observed_officer_name,
      item.observed_status,
      item.status,
      custodyReconciliationSummary(item),
    ].map((part) => value(part, '')).join(' ').toLowerCase();
    return query.includes(search.toLowerCase())
      && (!typeFilter || validationTypeKey(item) === typeFilter)
      && (!severityFilter || item.severity === severityFilter)
      && (!statusFilter || item.status === statusFilter)
      && (!departmentFilter || item.department_name === departmentFilter)
      && (!evidenceFilter || String(Boolean(item.evidence_available)) === evidenceFilter)
      && (!dateFrom || String(item.created_at || '').slice(0, 10) >= dateFrom)
      && (!dateTo || String(item.created_at || '').slice(0, 10) <= dateTo);
  }), [dateFrom, dateTo, departmentFilter, discrepancies, evidenceFilter, search, severityFilter, statusFilter, typeFilter]);

  const clearFilters = () => {
    setSearch('');
    setTypeFilter('');
    setSeverityFilter('');
    setStatusFilter('');
    setDepartmentFilter('');
    setEvidenceFilter('');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <div className="admin-dashboard inspector-workspace">
      {!embedded && <div className="page-heading"><div><h1>Validation Results</h1><p>Read-only discrepancy records generated by submitted inspections.</p></div></div>}
      <FRSNote>{allowActions
        ? 'Reconciliation maps to FRS 6.1. Lifecycle actions coordinate authorized correction without transferring custody.'
        : 'Reconciliation maps to FRS 6.1. Inspector access remains read-only.'}</FRSNote>
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      {actionError && !actionMode && !revalidationFailure && (
        <DismissibleError message={actionError} onDismiss={() => setActionError('')} />
      )}
      <WorkflowFeedback message={actionMessage} onDismiss={() => setActionMessage('')} />
      <RecordsTableCard
        title={recordsTitle}
        description={recordsDescription}
        meta={loading ? 'Loading' : `${visibleDiscrepancies.length} of ${discrepancies.length}`}
        toolbar={(
          <CompactTableToolbar
            searchValue={search}
            searchPlaceholder="type, serial, department, officer"
            onSearchChange={setSearch}
            filters={(
              <>
                <select aria-label="Type filter" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
                  <option value="">All discrepancy types</option>
                  {discrepancyTypeOptions.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                </select>
                <select aria-label="Severity filter" value={severityFilter} onChange={(event) => setSeverityFilter(event.target.value)}>
                  <option value="">All severity</option>
                  {VALIDATION_SEVERITY_OPTIONS.map((severity) => <option key={severity.value} value={severity.value}>{severity.label}</option>)}
                </select>
                <select aria-label="Status filter" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="">All statuses</option>
                  {DISCREPANCY_STATUS_OPTIONS.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                </select>
                <select aria-label="Department filter" value={departmentFilter} onChange={(event) => setDepartmentFilter(event.target.value)}><option value="">All departments</option>{departments.map((department) => <option key={department.id} value={department.name}>{department.name}</option>)}</select>
                <select aria-label="Evidence filter" value={evidenceFilter} onChange={(event) => setEvidenceFilter(event.target.value)}><option value="">Any evidence</option><option value="true">Evidence available</option><option value="false">No evidence</option></select>
                <input aria-label="Date from" type="date" value={dateFrom} max={dateTo || today()} onChange={(event) => setDateFrom(event.target.value)} />
                <input aria-label="Date to" type="date" value={dateTo} min={dateFrom || undefined} max={today()} onChange={(event) => setDateTo(event.target.value)} />
              </>
            )}
            actions={<button className="btn btn-secondary" type="button" onClick={clearFilters}>Clear</button>}
          />
        )}
      >
        <table className="records-table">
          <thead><tr><th>Type</th><th>Severity</th><th>Department</th><th>Serial</th><th>Expected</th><th>Observed</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8}><div className="loading-state"><span className="loading-mark" />Loading validation results...</div></td></tr>
            ) : visibleDiscrepancies.length === 0 ? (
              <tr><td colSpan={8}><EmptyTableState title="No discrepancies" description="Discrepancy rows will appear after submitted inspections require Logistics review." /></td></tr>
            ) : visibleDiscrepancies.map((item) => {
              const custodySummary = custodyReconciliationSummary(item);
              return (
                <tr className="clickable-row" key={item.id} onClick={() => setSelectedDiscrepancy(item)}>
                  <td>
                    <TypeBadge type={validationTypeKey(item)} label={validationTypeLabel(item)} tone={typeTone(validationTypeKey(item))} />
                    {custodySummary ? <div className="form-helper-muted">{custodySummary}</div> : null}
                  </td>
                  <td>{titleText(item.severity)}</td>
                  <td>{value(item.department_name)}</td>
                  <td>{value(item.source_serial_number)}</td>
                  <td>{expectedObservedValue(item.expected_officer_name, item.expected_status)}</td>
                  <td>{expectedObservedValue(item.observed_officer_name, item.observed_status)}</td>
                  <td><StatusBadge status={item.status || ''} label={titleText(item.status)} tone={statusTone(item.status)} /></td>
                  <td>
                    <button className="btn btn-secondary" type="button" onClick={(event) => { event.stopPropagation(); setSelectedDiscrepancy(item); }}>View Details</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </RecordsTableCard>
      <DetailDrawer
        open={Boolean(selectedDiscrepancy)}
        title="Validation Result Summary"
        subtitle={selectedDiscrepancy ? value(selectedDiscrepancy.source_serial_number) : undefined}
        meta={selectedDiscrepancy ? <TypeBadge type={validationTypeKey(selectedDiscrepancy)} label={validationTypeLabel(selectedDiscrepancy)} tone={typeTone(validationTypeKey(selectedDiscrepancy))} /> : null}
        onClose={() => { setSelectedDiscrepancy(null); setActionError(''); setRevalidationFailure(null); }}
        footer={allowActions && selectedDiscrepancy ? (
          <>
            {selectedDiscrepancy.can_retain && (
              <button className="btn btn-secondary" type="button" onClick={() => openAction('retain')}>
                Retain Authoritative Custody
              </button>
            )}
            {selectedDiscrepancy.can_refer && (
              <button className="btn btn-primary" type="button" onClick={() => openAction('refer')}>
                Refer for Custody Correction
              </button>
            )}
            {selectedDiscrepancy.can_revise_correction_plan && (
              <button className="btn btn-secondary" type="button" onClick={() => openAction('revise')}>
                Revise Correction Plan
              </button>
            )}
            {selectedDiscrepancy.can_revalidate && (
              <button className="btn btn-primary" type="button" disabled={actionLoading} onClick={() => void revalidate()}>
                Revalidate After Correction
              </button>
            )}
          </>
        ) : undefined}
      >
        {selectedDiscrepancy && (
          <div className="detail-summary-grid">
            <>
              <DetailField label="Type">{validationTypeLabel(selectedDiscrepancy)}</DetailField>
              <DetailField label="Severity">{titleText(selectedDiscrepancy.severity)}</DetailField>
              <DetailField label="Department">{value(selectedDiscrepancy.department_name)}</DetailField>
              <DetailField label="Serial">{value(selectedDiscrepancy.source_serial_number)}</DetailField>
              {custodyReconciliationDetailRows(selectedDiscrepancy).map(([label, detail]) => (
                <DetailField key={label} label={label}>{detail}</DetailField>
              ))}
              <DetailField label="Expected">
                {expectedObservedValue(
                  selectedDiscrepancy.expected_officer_name,
                  selectedDiscrepancy.expected_status,
                )}
              </DetailField>
              <DetailField label="Observed">
                {expectedObservedValue(
                  selectedDiscrepancy.observed_officer_name,
                  selectedDiscrepancy.observed_status,
                )}
              </DetailField>
              <DetailField label="Status">{titleText(selectedDiscrepancy.status)}</DetailField>
              <DetailField label="Validation Result ID">#{selectedDiscrepancy.id}</DetailField>
              <DetailField label="Reconciliation Report ID">#{selectedDiscrepancy.report}</DetailField>
              <DetailField label="Report Version">{value(selectedDiscrepancy.report_version)}</DetailField>
              {selectedDiscrepancy.submitted_inspection && (
                <DetailField label="Inspection Report ID">#{selectedDiscrepancy.submitted_inspection}</DetailField>
              )}
              <DetailField label="Original Finding">
                {value(selectedDiscrepancy.metadata?.message || selectedDiscrepancy.description)}
              </DetailField>
              <DetailField label="Correction target">
                {selectedDiscrepancy.correction_target_officer_name
                  ? `${selectedDiscrepancy.correction_target_officer_name}${selectedDiscrepancy.correction_target_officer_badge ? ` (${selectedDiscrepancy.correction_target_officer_badge})` : ''}`
                  : 'Not selected'}
              </DetailField>
              <DetailField label="Referred by">{value(selectedDiscrepancy.referred_by_username)}</DetailField>
              <DetailField label="Referred at">{formatDateTime(selectedDiscrepancy.referred_at)}</DetailField>
              <DetailField label="Referral notes">{value(selectedDiscrepancy.referral_notes)}</DetailField>
              <DetailField label="Correction workflow">
                {value(selectedDiscrepancy.correction_workflow_display)}
              </DetailField>
              <DetailField label="Correction Plan version">
                {selectedDiscrepancy.correction_plan_version || 'Not prescribed'}
              </DetailField>
              <DetailField label="Correction instruction">
                {value(selectedDiscrepancy.correction_instruction)}
              </DetailField>
              <DetailField label="Prescribed by">
                {value(selectedDiscrepancy.correction_prescribed_by_username)}
              </DetailField>
              <DetailField label="Prescribed at">
                {formatDateTime(selectedDiscrepancy.correction_prescribed_at)}
              </DetailField>
              <DetailField label="Resolution">{value(selectedDiscrepancy.resolution_type_display)}</DetailField>
              <DetailField label="Resolved by">{value(selectedDiscrepancy.resolved_by_username)}</DetailField>
              <DetailField label="Resolved at">{formatDateTime(selectedDiscrepancy.resolved_at)}</DetailField>
              <DetailField label="Resolution notes">{value(selectedDiscrepancy.resolution_notes)}</DetailField>
              <DetailField label="Inspection-time issuance">
                {selectedDiscrepancy.inspection_snapshot?.issuance_id
                  ? `#${selectedDiscrepancy.inspection_snapshot.issuance_id} - ${titleText(selectedDiscrepancy.inspection_snapshot.issuance_status)}`
                  : 'None'}
              </DetailField>
              <DetailField label="Current custody">
                {value(selectedDiscrepancy.current_custody?.expected_officer_name, 'No active custodian')}
              </DetailField>
              <DetailField label="Custody changed">
                {selectedDiscrepancy.custody_changed_after_inspection === null
                  || selectedDiscrepancy.custody_changed_after_inspection === undefined
                  ? 'Historical snapshot unavailable'
                  : selectedDiscrepancy.custody_changed_after_inspection
                    ? 'Yes'
                    : 'No'}
              </DetailField>
              <DetailField label="Evidence">
                {selectedDiscrepancy.evidence_available ? 'Available' : 'Not available'}
              </DetailField>
              <DetailField label="Created">
                {formatDateTime(selectedDiscrepancy.created_at)}
              </DetailField>
              {selectedDiscrepancy.resolution_snapshot?.matched_correction && (
                <DetailField label="Matched Correction">
                  {`${selectedDiscrepancy.resolution_snapshot.matched_correction.kind} #${selectedDiscrepancy.resolution_snapshot.matched_correction.id}`}
                </DetailField>
              )}
              {selectedDiscrepancy.resolution_snapshot?.correction_evaluation && (
                <DetailField label="Integrity Checks">
                  {selectedDiscrepancy.resolution_snapshot.correction_evaluation.satisfied
                    ? 'Passed'
                    : 'Not satisfied'}
                </DetailField>
              )}
              {allowActions && selectedDiscrepancy.status === 'REFERRED' && (
                <div className="full-width info-message">
                  <strong>Required follow-up</strong>
                  <p>This discrepancy cannot close until current authoritative custody is corrected through an authorized workflow and revalidated.</p>
                </div>
              )}
              {revalidationFailure && (
                <div className="full-width error-message" role="alert">
                  <strong>Correction not yet satisfied</strong>
                  {(revalidationFailure.evaluation?.reasons || revalidationFailure.reasons || [])
                    .map((reason: string) => <p key={reason}>- {reason}</p>)}
                  <p>Current custodian: {value(revalidationFailure.current_custody?.expected_officer_name, 'No active custodian')}</p>
                </div>
              )}
            </>

          </div>
        )}
      </DetailDrawer>
      <FormModal
        open={Boolean(actionMode)}
        title={actionMode === 'retain'
          ? 'Retain Authoritative Custody'
          : actionMode === 'revise'
            ? 'Revise Correction Plan'
            : 'Refer for Custody Correction'}
        description={actionMode === 'retain'
          ? 'Confirm that the existing authoritative custody record remains correct. This action does not transfer custody.'
          : 'This action does not change firearm custody. Use the authorized Return, Issuance, or correction workflow before revalidating.'}
        onClose={() => { if (!actionLoading) { setActionMode(null); setActionError(''); } }}
        footer={(
          <>
            <button className="btn btn-secondary" type="button" disabled={actionLoading} onClick={() => setActionMode(null)}>Cancel</button>
            <button className="btn btn-primary" type="button" disabled={actionLoading} onClick={() => void submitAction()}>
              {actionLoading
                ? 'Saving...'
                : actionMode === 'retain'
                  ? 'Retain Custody'
                  : actionMode === 'revise'
                    ? 'Revise Plan'
                    : 'Refer Discrepancy'}
            </button>
          </>
        )}
      >
        {actionError && <DismissibleError message={actionError} onDismiss={() => setActionError('')} />}
        <div className="inspector-form">
          {actionMode === 'retain' ? (
            <label className="full-width">
              <span>Resolution notes *</span>
              <textarea aria-label="Resolution notes" value={actionNotes} onChange={(event) => setActionNotes(event.target.value)} />
            </label>
          ) : (
            <>
              {actionMode === 'refer' && (
                <label className="full-width">
                  <span>Referral notes *</span>
                  <textarea aria-label="Referral notes" value={actionNotes} onChange={(event) => setActionNotes(event.target.value)} />
                </label>
              )}
              <label>
                <span>Correction workflow *</span>
                <select
                  aria-label="Correction workflow"
                  value={correctionWorkflow}
                  onChange={(event) => {
                    const next = event.target.value as 'ISSUANCE' | 'RETURN' | 'UNSPECIFIED';
                    setCorrectionWorkflow(next);
                    if (next === 'RETURN') setTargetOfficerId(null);
                  }}
                >
                  <option value="ISSUANCE">Issuance</option>
                  <option value="RETURN">Return</option>
                  <option value="UNSPECIFIED">No specific transaction prescribed</option>
                </select>
              </label>
              <label>
                <span>Affected firearm</span>
                <input aria-label="Affected firearm" value={selectedDiscrepancy?.source_serial_number || ''} readOnly />
              </label>
              {correctionWorkflow !== 'RETURN' && (
                <label className="full-width">
                  <span>{correctionWorkflow === 'ISSUANCE' ? 'Target officer *' : 'Target officer (optional)'}</span>
                  <select aria-label="Correction target" value={targetOfficerId || ''} onChange={(event) => setTargetOfficerId(Number(event.target.value) || null)}>
                    <option value="">{correctionWorkflow === 'ISSUANCE' ? 'Select target officer' : 'No target constraint'}</option>
                    {correctionOfficerOptions.map((officer: any) => (
                      <option key={officer.id} value={officer.id}>
                        {officerName(officer)}{officer.badge_number ? ` (${officer.badge_number})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="full-width">
                <span>Correction instruction *</span>
                <textarea aria-label="Correction instruction" value={correctionInstruction} onChange={(event) => setCorrectionInstruction(event.target.value)} />
              </label>
            </>
          )}
        </div>
      </FormModal>
    </div>
  );
};

export const ReconciliationDiscrepancies = ValidationResults;

export const InspectorReports: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  // Supported formal inspection and reconciliation PDF artifacts.
  const [reports, setReports] = React.useState<any[]>([]);
  const [inspections, setInspections] = React.useState<any[]>([]);
  const [reconciliations, setReconciliations] = React.useState<any[]>([]);
  const [selectedInspection, setSelectedInspection] = React.useState(0);
  const [selectedReconciliation, setSelectedReconciliation] = React.useState(0);
  const [error, setError] = React.useState('');
  const [generating, setGenerating] = React.useState('');

  const load = React.useCallback(async () => {
    const [generated, inspectionRows, reconciliationRows] = await Promise.all([
      inspectionService.getGeneratedReports(),
      inspectionService.getInspectionSubmissions(),
      inspectionService.getReconciliationReports(),
    ]);
    setReports(generated.filter((report: any) => APPROVED_REPORT_TYPES.has(report.report_type)));
    setInspections(inspectionRows);
    setReconciliations(reconciliationRows);
    setSelectedInspection((current) => current || inspectionRows[0]?.id || 0);
    setSelectedReconciliation((current) => current || reconciliationRows[0]?.id || 0);
  }, []);

  React.useEffect(() => { void load().catch(() => setError('Generated reports are temporarily unavailable.')); }, [load]);

  const generateReconciliation = async () => {
    if (!selectedReconciliation) return setError('Select a reconciliation report first.');
    await inspectionService.generateReconciliationPdf(selectedReconciliation);
    await load();
  };

  const generateCompliance = async () => {
    if (!selectedInspection) return setError('Select a submitted inspection first.');
    setGenerating('compliance');
    setError('');
    try {
      await inspectionService.generateCompliancePdf(selectedInspection);
      await load();
    } catch {
      setError('The formal inspection and compliance report could not be generated.');
    } finally {
      setGenerating('');
    }
  };

  const inspectionOptionLabel = (inspection: any): string => {
    const date = formatDate(inspection.actual_inspection_date);
    const unit = value(inspection.department_name, 'Unknown department');
    const serial = value(inspection.firearm_serial_number, 'Serial not recorded');
    return `#${inspection.id} • ${date} • ${unit} • ${serial}`;
  };

  const reconciliationOptionLabel = (report: any): string => `Validation Result Set #${report.id}`;

  const reconciliationDetailLabel = (report?: any): string => {
    if (!report) return '';
    const date = value(report.mission_date_of_physical_count || report.generated_at || report.created_at);
    const unit = value(report.mission_target_station_unit, 'Unknown department');
    const count = Number(report.discrepancy_count || 0);
    return `Date: ${date} • Department: ${unit} • ${count} ${count === 1 ? 'discrepancy' : 'discrepancies'}`;
  };

  const selectedReconciliationRecord = reconciliations.find((report: any) => report.id === selectedReconciliation);

  const generationControls = (
    <div className="generated-files-toolbar">
      <p className="generated-files-helper">Select a source record, then generate a PDF. These dropdowns do not filter the table below.</p>
      <div className="generated-source-card">
        <h3>Formal Inspection Report</h3>
        <label><span>Inspection source</span><select value={selectedInspection} onChange={(e) => setSelectedInspection(Number(e.target.value))}>{inspections.length === 0 ? <option value={0} disabled>No submitted inspection available</option> : <option value={0}>Select submitted inspection</option>}{inspections.map((inspection) => <option key={inspection.id} value={inspection.id}>{inspectionOptionLabel(inspection)}</option>)}</select></label>
        <button className="btn btn-primary" type="button" onClick={generateCompliance} disabled={!selectedInspection || Boolean(generating)}><FileText size={14} />{generating === 'compliance' ? 'Generating...' : 'Generate formal inspection PDF'}</button>
      </div>
      <div className="generated-source-card">
        <h3>Discrepancy PDF</h3>
        <label>
          <span>Validation result source for Discrepancy PDF</span>
          <select value={selectedReconciliation} onChange={(e) => setSelectedReconciliation(Number(e.target.value))}>{reconciliations.length === 0 ? <option value={0} disabled>No reconciliation source available</option> : <option value={0}>Select validation result source</option>}{reconciliations.map((report) => <option key={report.id} value={report.id}>{reconciliationOptionLabel(report)}</option>)}</select>
          {selectedReconciliationRecord && <small className="generated-source-detail">{reconciliationDetailLabel(selectedReconciliationRecord)}</small>}
        </label>
        <button className="btn btn-secondary" type="button" onClick={generateReconciliation} disabled={!selectedReconciliation}><FileText size={14} />Generate secure Discrepancy PDF</button>
      </div>
    </div>
  );

  return (
    <div className={embedded ? 'inspector-workspace generated-files-workspace' : 'admin-dashboard inspector-workspace generated-files-workspace'}>
      {!embedded && <div className="page-heading"><div><h1>Generated Report Files</h1><p>Submitted inspection and discrepancy PDFs when backend generation is available.</p></div></div>}
      <FRSNote>Reports preserve immutable inspection facts and the reconciliation lifecycle.</FRSNote>
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <RecordsTableCard
        title="Generated Report Files"
        description="Protected formal inspection and reconciliation reports."
        meta={`${reports.length} file${reports.length === 1 ? '' : 's'}`}
        toolbar={generationControls}
        className="generated-files-card"
      >
        <table className="records-table generated-files-table">
          <thead><tr><th>Type</th><th>Version</th><th>Filename</th><th>Status</th><th>Generated By</th><th>Date Generated</th><th>Download</th></tr></thead>
          <tbody>
            {reports.length === 0 ? (
              <tr><td colSpan={7}><EmptyTableState title="No approved reports found" description="Generated inspection reports will appear here." /></td></tr>
            ) : reports.map((report) => (
              <tr key={report.id}>
                <td><TypeBadge type={report.report_type || ''} label={report.report_type === 'inspection_compliance' ? 'Formal Inspection Report' : 'Reconciliation Report'} tone={report.report_type === 'inspection_compliance' ? 'green' : 'purple'} /></td>
                <td>{report.version || 1}</td>
                <td>{value(report.filename)}</td>
                <td><StatusBadge status={report.status || ''} label={titleText(report.status)} /></td>
                <td>{value(report.generated_by_name || report.generated_by_username || report.generated_by, 'System')}</td>
                <td>{formatDate(report.generated_at || report.created_at)}</td>
                <td>{report.download_url ? <a href={report.download_url}>Download</a> : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </RecordsTableCard>
    </div>
  );
};

export const InspectorNotifications: React.FC = () => {
  // FRS 8: existing notification permissions only; no extra notification permissions granted.
  const [notifications, setNotifications] = React.useState<any[]>([]);
  const [error, setError] = React.useState('');
  const [activeFilter, setActiveFilter] = React.useState<NotificationFilter>('all');
  const [search, setSearch] = React.useState('');
  const [selectedNotification, setSelectedNotification] = React.useState<(NotificationInboxItem & { category?: NotificationFilter }) | null>(null);

  React.useEffect(() => {
    notificationService.getSystemNotifications().then(setNotifications).catch(() => setError('Notifications are unavailable or not authorized.'));
  }, []);

  const openNotification = (item: NotificationInboxItem & { category?: NotificationFilter }) => {
    const previousStatus = item.status;
    const readItem = { ...item, status: 'read' };
    setSelectedNotification(readItem);
    setNotifications((current) => current.map((notification) =>
      notification.id === item.id ? { ...notification, status: 'read' } : notification
    ));
    void markNotificationReadIfUnread(item.id, previousStatus)
      .catch(() => {
        setNotifications((current) => current.map((notification) =>
          notification.id === item.id ? { ...notification, status: previousStatus } : notification
        ));
        setSelectedNotification((current) => (
          current?.id === item.id ? { ...current, status: previousStatus } : current
        ));
      });
  };

  const inboxItems = React.useMemo(() => notifications.map((notification): NotificationInboxItem & { category: NotificationFilter } => {
    const notificationType = notification.notification_type || notification.type || 'system';
    const category = notificationCategory(notification);
    const title = notification.title || notificationLabel(notificationType);
    return {
      ...notification,
      id: notification.id,
      title,
      message: notification.message || `${title} requires review.`,
      notification_type: notificationType,
      severity: notification.severity || (category === 'validation' ? 'high' : 'info'),
      status: notification.status,
      related_context: relatedNotificationContext(notification),
      actionLabel: 'View Details',
      category,
    };
  }), [notifications]);

  const visibleItems = React.useMemo(() => inboxItems.filter((item) => {
    const query = [
      item.title,
      item.message,
      item.notification_type,
      item.status,
      item.severity,
      item.officer_name,
      item.officer_badge,
      item.related_context,
    ].map((part) => value(part, '')).join(' ').toLowerCase();
    return (activeFilter === 'all' || item.category === activeFilter) && query.includes(search.toLowerCase());
  }), [activeFilter, inboxItems, search]);

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading"><div><h1>Notifications</h1><p>Existing authorized Inspector notifications.</p></div></div>
      <FRSNote>Notifications maps to FRS 8 only when existing RBAC permission is present.</FRSNote>
      {error ? <DismissibleError message={error} onDismiss={() => setError('')} /> : (
        <NotificationInbox
          title="Inspector Notification Inbox"
          items={visibleItems}
          emptyTitle="No notifications"
          emptyDescription="Notification delivery will appear here when authorized records exist."
          onSelect={(item) => openNotification(item as NotificationInboxItem & { category?: NotificationFilter })}
          toolbar={(
            <CompactTableToolbar
              searchValue={search}
              searchPlaceholder="Search notification, officer, firearm, or department"
              onSearchChange={setSearch}
              filters={(
                <div className="notification-filter-tabs" role="tablist" aria-label="Notification filters">
                  {NOTIFICATION_FILTERS.map((filter) => (
                    <button
                      key={filter.value}
                      type="button"
                      role="tab"
                      aria-selected={activeFilter === filter.value}
                      className={activeFilter === filter.value ? 'active' : ''}
                      onClick={() => setActiveFilter(filter.value)}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              )}
              resultCount={`${visibleItems.length} of ${inboxItems.length}`}
            />
          )}
        />
      )}
      <DetailDrawer
        open={Boolean(selectedNotification)}
        title={selectedNotification?.title || 'Notification Details'}
        subtitle={selectedNotification?.related_context || undefined}
        meta={selectedNotification?.severity ? <TypeBadge type={selectedNotification.severity} tone={typeTone(selectedNotification.severity)} /> : null}
        onClose={() => setSelectedNotification(null)}
      >
        {selectedNotification && (
          <div className="detail-summary-grid">
            <DetailField label="Message">{value(selectedNotification.message)}</DetailField>
            <DetailField label="Type">{notificationLabel(selectedNotification.notification_type || selectedNotification.type)}</DetailField>
            <DetailField label="Status">{titleText(selectedNotification.status)}</DetailField>
            <DetailField label="Severity">{titleText(selectedNotification.severity)}</DetailField>
            <DetailField label="Related">{value(selectedNotification.related_context?.replace(/^Related: /, ''), 'No related record listed')}</DetailField>
            <DetailField label="Officer">{value(selectedNotification.officer_name || selectedNotification.officer_badge, 'No officer listed')}</DetailField>
            <DetailField label="Date">{formatDate(selectedNotification.due_date || selectedNotification.generated_at || selectedNotification.created_at)}</DetailField>
          </div>
        )}
      </DetailDrawer>
    </div>
  );
};
